/**
 * SurveyQuestionsPreview Component
 * 
 * Displays AI-parsed survey questions in editable form for user review and modification
 * Specifically designed for the AI Interviewer feature
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Edit3, Save, X, AlertCircle, CheckCircle, ArrowLeft, Trash2, GripVertical, Plus, Copy, Eye } from 'lucide-react';
import Button from '../../components/Button';
import { UI_TEXT } from '../../config/ui-text';
import { PANEL_PROVIDERS } from '../../config/panel-providers';
import ImageHotspotEditor, { ImageHotspotConfig } from '../../components/survey/ImageHotspotEditor';
import MediaUploadGrid, {
  getMediaTypeFromUrl,
  isExternalStreamingVideoUrl,
  getYouTubeThumbnail,
  getEmbedUrl,
} from '../../components/survey/MediaUploadGrid';
import MediaUploadIcon from '../../components/survey/MediaUploadIcon';
import GroupMediaUpload, { getGroupColor } from '../../components/survey/GroupMediaUpload';
import { getGroupLabel } from '../../utils/groupUtils';
import { surveyIdToShareId, type SurveyBlockDefinition } from '../../utils/surveyApi';
import QuestionText from '../../components/common/QuestionText';

interface ChoiceAction {
  value: string;
  action?: 'terminate' | 'skip_to';
  target?: string;  // question ID for skip_to
  reason?: string;  // reason for terminate
}

interface ParsedQuestion {
  id: string;
  text: string;
  type: string;
  choices?: string[];
  choice_actions?: ChoiceAction[];  // Action configuration for screener/skip logic
  scale_min?: number;
  scale_max?: number;
  max_selections?: number;
  ranking_count?: number;
  text_limit?: number;
  required?: boolean;
  followup_instructions?: string;
  allow_other?: boolean;
  enable_ai_followup?: boolean;
  randomize_choices?: boolean;
  /** Non-null = member of named shuffle pool (e.g. default, pool_a); questions shuffle only within the same pool. */
  randomization_group?: string | null;
  /** Survey block id for grouping / block-level randomization; omit or null = unblocked. */
  block_id?: string | null;
  // New question type fields
  matrix_rows?: string[];
  matrix_columns?: string[];
  conjoint_attributes?: { name: string; levels: string[] }[];
  media_urls?: string[];
  randomize_media_order?: boolean;
  /** @deprecated Use randomize_media_order instead. Kept for backward compat with saved data. */
  image_position_mode?: 'manual' | 'randomized' | null;
  /** @deprecated Drag-and-drop reorders media_urls directly now. Kept for backward compat. */
  image_order?: number[] | null;
  media_display_mode?: 'question' | 'choices';  // How to display media_urls for choice questions
  option_media?: { [optionIndex: number]: string[] };  // Media per option (for choice questions)
  description?: string;
  // Info Screen fields
  display_time_seconds?: number;  // For info_screen: minimum time before continue button is enabled
  // Image Hotspot fields
  image_url?: string;
  image_path?: string;
  bounding_boxes?: { id: string; label: string; x: number; y: number; width: number; height: number }[];
  free_click_mode?: boolean;
  // Between-subject group fields
  visible_groups?: number[];
  group_media?: Record<string, string[]>;
  option_group_media?: Record<string, Record<string, string[]>>;
  // Percentage allocation fields
  allocation_options?: string[];
  allocation_target?: number;
  auto_fill_last?: boolean;
  allow_zero?: boolean;
  // Conditional display (AND/OR conditions)
  display_condition?: {
    logic?: 'and' | 'or';
    conditions?: Array<{
      source_question_id: string;
      operator: 'selected' | 'not_selected' | '>' | '>=' | '=' | '<' | '<=';
      value: string;
      option_key?: string;
      threshold?: number;
    }>;
    // Legacy flat fields (backward compat)
    source_question_id?: string;
    operator?: 'selected' | 'not_selected' | '>' | '>=' | '=' | '<' | '<=';
    value?: string;
    option_key?: string;
    threshold?: number;
  };
}

interface RedirectConfig {
  redirect_base_url: string;
  screened_out_url?: string | null;  // Optional: separate URL for screened out participants
  tracking_param: { [key: string]: string };
  completion_params: { [key: string]: string } | null;
  screen_out_params: { [key: string]: string } | null;
  quota_full_params: { [key: string]: string } | null;
}

/** Checks if a question has randomize_media_order set (including backward compat with old image_position_mode). */
function shouldRandomizeMedia(q: ParsedQuestion): boolean {
  if (q.randomize_media_order != null) return q.randomize_media_order;
  return q.image_position_mode === 'randomized';
}

interface SurveyQuestionsPreviewProps {
  parsedQuestions: ParsedQuestion[];
  surveyName: string;
  onQuestionsUpdated: (
    questions: ParsedQuestion[],
    redirectConfig?: RedirectConfig | null,
    maxResponses?: number | null,
    blocks?: SurveyBlockDefinition[] | null,
    minLinkedInConnections?: number | null
  ) => void;
  onCreateSurvey: (
    surveyName: string,
    questions: ParsedQuestion[],
    redirectConfig?: RedirectConfig | null,
    maxResponses?: number | null,
    blocks?: SurveyBlockDefinition[] | null,
    minLinkedInConnections?: number | null
  ) => Promise<void>;
  onGoBack: () => void;
  isLoading?: boolean;
  className?: string;
  readOnly?: boolean;
  hideActionButtons?: boolean;
  initialMaxResponses?: number | null;
  initialMinLinkedInConnections?: number | null;
  initialRedirectConfig?: RedirectConfig | null;
  /** Block definitions from survey API; omit to keep local block state only (e.g. new survey builder). */
  initialBlocks?: SurveyBlockDefinition[] | null;
  groupCount?: number;
  surveyId?: number | null;
  /** For group surveys: all child survey IDs (parallel to groupLabels) */
  groupSurveyIds?: number[];
  /** For group surveys: labels for each group */
  groupLabels?: string[];
}

// All supported question types
const QUESTION_TYPES = [
  { value: 'info_screen', label: 'Information Screen' },
  { value: 'single_choice', label: UI_TEXT.AI_SURVEY.QUESTIONS_PREVIEW.QUESTION_TYPES.SINGLE_CHOICE },
  { value: 'multiple_choice', label: UI_TEXT.AI_SURVEY.QUESTIONS_PREVIEW.QUESTION_TYPES.MULTIPLE_CHOICE },
  { value: 'yes_no', label: 'Yes/No' },
  { value: 'rating_scale', label: 'Rating Scale' },
  { value: 'likert_scale', label: 'Likert Scale' },
  { value: 'ranking', label: 'Ranking' },
  { value: 'short_text', label: 'Short Text' },
  { value: 'long_text', label: 'Long Text' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'nps', label: 'Net Promoter Score' },
  { value: 'numeric_input', label: 'Numeric Input' },
  { value: 'matrix_rating', label: 'Matrix/Grid' },
  { value: 'max_diff', label: 'MaxDiff (Best/Worst)' },
  { value: 'conjoint_analysis', label: 'Conjoint Analysis (Choice-Based)' },
  { value: 'image_hotspot', label: 'Image Hotspot (Click Ranking)' },
  { value: 'percentage_allocation', label: 'Percentage Allocation' },
  { value: 'photo_upload', label: 'Photo Upload (AI Product Extraction)' },
];

// Question types that support answer choices (mutually exclusive with scale-based types)
const CHOICE_BASED_TYPES = ['single_choice', 'multiple_choice', 'yes_no', 'dropdown', 'ranking'];

// Question types that support scale settings
const SCALE_BASED_TYPES = ['rating_scale', 'likert_scale', 'nps'];

// Question types that support ranking settings
const RANKING_BASED_TYPES = ['ranking'];

// Question types that support allocation settings
const ALLOCATION_BASED_TYPES = ['percentage_allocation'];

// Question types that support matrix settings
const MATRIX_BASED_TYPES = ['matrix_rating'];

// Question types that support MaxDiff settings
const MAX_DIFF_BASED_TYPES = ['max_diff'];

// Question types that support Conjoint Analysis settings
const CONJOINT_BASED_TYPES = ['conjoint_analysis'];

// Question types that support Image Hotspot settings
const IMAGE_HOTSPOT_TYPES = ['image_hotspot'];

// Question types that support Photo Upload settings
const PHOTO_UPLOAD_TYPES = ['photo_upload'];

// Question types that support media content (images/videos)
// Note: image_hotspot and photo_upload have their own specialized media handling
const MEDIA_SUPPORTED_TYPES = [
  'info_screen',
  'single_choice',
  'multiple_choice',
  'yes_no',
  'rating_scale',
  'likert_scale',
  'ranking',
  'short_text',
  'long_text',
  'dropdown',
  'nps',
  'numeric_input',
  'matrix_rating',
  'max_diff',
  'conjoint_analysis',
  'percentage_allocation',
];

// Per-pool badge colors (cycle when there are many pools)
const RANDOMIZATION_POOL_BADGE_CLASSES = [
  'bg-violet-100 text-violet-900 border-violet-300',
  'bg-amber-100 text-amber-900 border-amber-300',
  'bg-emerald-100 text-emerald-900 border-emerald-300',
  'bg-sky-100 text-sky-900 border-sky-300',
  'bg-rose-100 text-rose-900 border-rose-300',
  'bg-indigo-100 text-indigo-900 border-indigo-300',
];

function formatRandomizationPoolLabel(poolId: string): string {
  if (poolId === 'default') return 'Default';
  const m = /^pool_([a-z])$/i.exec(poolId);
  if (m) return `Pool ${m[1].toUpperCase()}`;
  return poolId.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function nextRandomizationPoolId(existingIds: string[]): string {
  const lower = new Set(existingIds.map((id) => id.toLowerCase()));
  for (let i = 0; i < 26; i++) {
    const ch = String.fromCharCode(97 + i);
    const id = `pool_${ch}`;
    if (!lower.has(id)) return id;
  }
  return `pool_${Date.now()}`;
}

function randomizationPoolBadgeClass(poolId: string, orderedPoolIds: string[]): string {
  const idx = orderedPoolIds.indexOf(poolId);
  const i = idx >= 0 ? idx : 0;
  return RANDOMIZATION_POOL_BADGE_CLASSES[i % RANDOMIZATION_POOL_BADGE_CLASSES.length];
}

const BLOCK_ACCENT_PALETTES = [
  'border-l-teal-500 bg-teal-50/60',
  'border-l-indigo-500 bg-indigo-50/60',
  'border-l-amber-500 bg-amber-50/60',
  'border-l-rose-500 bg-rose-50/60',
];

function blockAccentClass(blockId: string | null | undefined): string {
  if (!blockId) return '';
  let h = 0;
  for (let i = 0; i < blockId.length; i++) h = (h + blockId.charCodeAt(i)) % BLOCK_ACCENT_PALETTES.length;
  return `border-l-4 ${BLOCK_ACCENT_PALETTES[h]}`;
}

// Question types that support conditional actions (screener/skip logic)
const CONDITIONAL_ACTION_TYPES = ['single_choice', 'multiple_choice', 'yes_no', 'dropdown'];

// Question types that can be used as source for display conditions
const DISPLAY_CONDITION_TYPES = ['single_choice', 'multiple_choice', 'yes_no', 'dropdown', 'percentage_allocation'];

const SurveyQuestionsPreview: React.FC<SurveyQuestionsPreviewProps> = ({
  parsedQuestions,
  surveyName,
  onQuestionsUpdated,
  onCreateSurvey,
  onGoBack,
  isLoading = false,
  className = '',
  readOnly = false,
  hideActionButtons = false,
  initialMaxResponses,
  initialMinLinkedInConnections,
  initialRedirectConfig,
  initialBlocks,
  groupCount = 1,
  surveyId = null,
  groupSurveyIds = [],
  groupLabels = [],
}) => {
  const [questions, setQuestions] = useState<ParsedQuestion[]>([]);
  const [blocks, setBlocks] = useState<SurveyBlockDefinition[]>([]);
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null);
  const [originalQuestionBeforeEdit, setOriginalQuestionBeforeEdit] = useState<ParsedQuestion | null>(null);
  const [editingBlockId, setEditingBlockId] = useState<string | null>(null);
  const [editingBlockConditionId, setEditingBlockConditionId] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Preview group picker: stores the question index (or -1 for full preview) while user picks a group
  const [previewPickerFor, setPreviewPickerFor] = useState<number | null>(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState<string | null>(null);

  // Drag and drop state
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);

  // Response quota configuration
  const [maxResponses, setMaxResponses] = useState<string>('');
  const [showQuotaConfig, setShowQuotaConfig] = useState(false);

  // LinkedIn verification configuration
  const [minLinkedInConnections, setMinLinkedInConnections] = useState<string>('');

  // Panel provider redirect configuration
  const [showRedirectConfig, setShowRedirectConfig] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<string>('');
  const [redirectBaseUrl, setRedirectBaseUrl] = useState('');
  const [screenedOutUrl, setScreenedOutUrl] = useState('');  // Separate URL for screened out participants
  const [trackingParamName, setTrackingParamName] = useState('');
  const [completionParams, setCompletionParams] = useState<string>('');
  const [screenOutParams, setScreenOutParams] = useState<string>('');
  const [quotaFullParams, setQuotaFullParams] = useState<string>('');

  // Media panel expansion state
  const [expandedQuestionMedia, setExpandedQuestionMedia] = useState<string | null>(null);
  const [expandedOptionMedia, setExpandedOptionMedia] = useState<{ questionId: string; optionIndex: number } | null>(null);

  const randomizationPoolsInSurvey = useMemo(() => {
    const s = new Set<string>();
    for (const q of questions) {
      if (q.randomization_group) s.add(q.randomization_group);
    }
    return Array.from(s).sort();
  }, [questions]);

  // Handler for provider selection - auto-fills all fields
  const handleProviderChange = (providerId: string) => {
    setSelectedProvider(providerId);

    if (providerId && PANEL_PROVIDERS[providerId]) {
      const provider = PANEL_PROVIDERS[providerId];
      setRedirectBaseUrl(provider.redirectBaseUrl);
      setScreenedOutUrl(provider.screenedOutUrl);
      setTrackingParamName(provider.trackingParamName);
      setCompletionParams(provider.completionParams);
      setScreenOutParams(provider.screenOutParams);
      setQuotaFullParams(provider.quotaFullParams);
    }
  };

  useEffect(() => {
    // Skip state updates if user is actively editing a question OR has unsaved changes
    // This prevents polling (e.g., in SurveyGroupDetails) from blowing away edits in progress
    if (editingQuestionId || hasChanges) {
      return;
    }

    // Map qualitative questions to long_text and set default enable_ai_followup
    const processedQuestions = parsedQuestions.map(q => ({
      ...q,
      type: q.type === 'qualitative' ? 'long_text' : q.type,
      enable_ai_followup: q.enable_ai_followup ?? false // Disabled by default - users can enable if needed
    }));
    setQuestions(processedQuestions);
    // Note: Don't reset hasChanges here - it should only be reset when user explicitly saves

    if (initialBlocks !== undefined) {
      setBlocks(initialBlocks ?? []);
    }

    // Auto-open image_hotspot questions in edit mode if they don't have an image configured
    const firstHotspotWithoutImage = processedQuestions.find(
      q => q.type === 'image_hotspot' && !q.image_url
    );
    if (firstHotspotWithoutImage && !readOnly) {
      setEditingQuestionId(firstHotspotWithoutImage.id);
      setOriginalQuestionBeforeEdit(firstHotspotWithoutImage);
    }
  }, [parsedQuestions, initialBlocks, readOnly, editingQuestionId, hasChanges]);

  // Auto-expand sections in readonly mode
  useEffect(() => {
    if (readOnly) {
      setShowQuotaConfig(true);
      setShowRedirectConfig(true);
    }
  }, [readOnly]);

  // Initialize quota, LinkedIn, and redirect config from props
  useEffect(() => {
    if (initialMaxResponses !== undefined && initialMaxResponses !== null) {
      setMaxResponses(initialMaxResponses.toString());
    }

    if (initialMinLinkedInConnections !== undefined && initialMinLinkedInConnections !== null) {
      setMinLinkedInConnections(initialMinLinkedInConnections.toString());
    }

    if (initialRedirectConfig) {
      setRedirectBaseUrl(initialRedirectConfig.redirect_base_url || '');
      setScreenedOutUrl(initialRedirectConfig.screened_out_url || '');

      // Extract tracking param name (first key in tracking_param object)
      const trackingKeys = Object.keys(initialRedirectConfig.tracking_param || {});
      if (trackingKeys.length > 0) {
        setTrackingParamName(trackingKeys[0]);
      }

      // Convert param objects to comma-separated strings
      const formatParams = (params: { [key: string]: string } | null) => {
        if (!params) return '';
        return Object.entries(params).map(([key, value]) => `${key}=${value}`).join(', ');
      };

      setCompletionParams(formatParams(initialRedirectConfig.completion_params));
      setScreenOutParams(formatParams(initialRedirectConfig.screen_out_params));
      setQuotaFullParams(formatParams(initialRedirectConfig.quota_full_params));
    }
  }, [initialMaxResponses, initialMinLinkedInConnections, initialRedirectConfig]);

  const openPreview = (groupSurveyId: number, startFrom?: number) => {
    const shareId = surveyIdToShareId(groupSurveyId);
    const params = startFrom !== undefined ? `?preview=true&startFrom=${startFrom}` : '?preview=true';
    window.open(`/surveys/${shareId}/take${params}`, '_blank');
    setPreviewPickerFor(null);
  };

  const renderVideoThumb = (url: string, fullUrl?: string) => {
    const thumb = getYouTubeThumbnail(url);
    const previewSrc = isExternalStreamingVideoUrl(url) ? (getEmbedUrl(url) || url) : (fullUrl || url);
    return (
      <button
        type="button"
        onClick={() => setVideoPreviewUrl(previewSrc)}
        className="w-full h-full relative bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center"
      >
        {thumb && (
          <img src={thumb} alt="Video" className="absolute inset-0 w-full h-full object-cover opacity-80" />
        )}
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-0.5">
          <svg className="w-4 h-4 text-white/70" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18" />
            <line x1="7" y1="2" x2="7" y2="22" />
            <line x1="17" y1="2" x2="17" y2="22" />
            <line x1="2" y1="12" x2="22" y2="12" />
            <line x1="2" y1="7" x2="7" y2="7" />
            <line x1="2" y1="17" x2="7" y2="17" />
            <line x1="17" y1="7" x2="22" y2="7" />
            <line x1="17" y1="17" x2="22" y2="17" />
          </svg>
          <span className="w-4 h-4 bg-white/90 rounded-full flex items-center justify-center">
            <span className="ml-0.5 border-l-[5px] border-l-gray-800 border-y-[3px] border-y-transparent" />
          </span>
        </div>
      </button>
    );
  };

  const handlePreviewClick = (questionIndex?: number) => {
    const isGroup = groupSurveyIds.length > 1;
    const targetId = isGroup ? null : surveyId;

    if (!isGroup && targetId) {
      openPreview(targetId, questionIndex);
    } else if (isGroup) {
      setPreviewPickerFor(questionIndex ?? -1);
    }
  };

  const updateQuestion = (questionId: string, updates: Partial<ParsedQuestion>) => {
    const updatedQuestions = questions.map(q =>
      q.id === questionId ? { ...q, ...updates } : q
    );
    setQuestions(updatedQuestions);
    setHasChanges(true);
    // Don't save to parent until user clicks save
  };

  const removeQuestion = (questionId: string) => {
    const conditionReferencesQuestion = (dc: any, qid: string): boolean => {
      if (!dc) return false;
      if (dc.source_question_id === qid) return true;
      if (Array.isArray(dc.conditions)) {
        return dc.conditions.some((c: any) => c.source_question_id === qid);
      }
      return false;
    };

    const dependentQuestions = questions.filter(
      q => conditionReferencesQuestion(q.display_condition, questionId)
    );

    let confirmMessage = 'Are you sure you want to delete this question? This action cannot be undone.';
    if (dependentQuestions.length > 0) {
      const questionIndex = questions.findIndex(q => q.id === questionId) + 1;
      const dependentList = dependentQuestions
        .map(q => `Q${questions.findIndex(dq => dq.id === q.id) + 1}`)
        .join(', ');
      confirmMessage = `Question Q${questionIndex} is used as a condition source for: ${dependentList}.\n\nDeleting this question will remove the display conditions from these dependent questions.\n\nAre you sure you want to continue?`;
    }

    if (window.confirm(confirmMessage)) {
      const updatedQuestions = questions
        .filter(q => q.id !== questionId)
        .map(q => {
          if (!conditionReferencesQuestion(q.display_condition, questionId)) return q;
          const dc = q.display_condition;
          if (dc?.source_question_id === questionId) {
            const { display_condition, ...rest } = q;
            return rest as ParsedQuestion;
          }
          if (Array.isArray(dc?.conditions)) {
            const remaining = dc.conditions.filter((c: any) => c.source_question_id !== questionId);
            if (remaining.length === 0) {
              const { display_condition, ...rest } = q;
              return rest as ParsedQuestion;
            }
            return { ...q, display_condition: { ...dc, conditions: remaining } };
          }
          return q;
        });

      setQuestions(updatedQuestions);
      setHasChanges(true);
      onQuestionsUpdated(updatedQuestions, undefined, undefined, blocks);

      // If we were editing this question, clear edit state
      if (editingQuestionId === questionId) {
        setEditingQuestionId(null);
        setOriginalQuestionBeforeEdit(null);
      }
    }
  };

  const addNewQuestion = () => {
    insertQuestionAt(questions.length);
  };

  // Insert a new question at a specific index
  const insertQuestionAt = (index: number) => {
    const newQuestion: ParsedQuestion = {
      id: `question_${Date.now()}`,
      text: '',
      type: 'single_choice',
      choices: ['Option 1', 'Option 2'],
      required: true,
      enable_ai_followup: false,
    };

    const updatedQuestions = [
      ...questions.slice(0, index),
      newQuestion,
      ...questions.slice(index)
    ];
    setQuestions(updatedQuestions);
    setHasChanges(true);
    onQuestionsUpdated(updatedQuestions, undefined, undefined, blocks);

    // Automatically open the new question for editing
    setOriginalQuestionBeforeEdit(newQuestion);
    setEditingQuestionId(newQuestion.id);
  };

  // Duplicate an existing question with all its settings
  const duplicateQuestion = (questionId: string) => {
    try {
      const sourceQuestion = questions.find(q => q.id === questionId);
      if (!sourceQuestion) {
        console.error('Cannot duplicate: question not found', questionId);
        return;
      }

      // Deep clone using structuredClone (modern, type-safe, handles edge cases)
      const duplicatedQuestion: ParsedQuestion = {
        ...structuredClone(sourceQuestion),
        id: `question_${crypto.randomUUID()}`,
      };

      // For image_hotspot questions, regenerate bounding box IDs to avoid selection state conflicts
      if (duplicatedQuestion.type === 'image_hotspot' && duplicatedQuestion.bounding_boxes) {
        duplicatedQuestion.bounding_boxes = duplicatedQuestion.bounding_boxes.map((box) => ({
          ...box,
          id: `box_${crypto.randomUUID()}`
        }));
      }

      // Find index and insert after the original
      const sourceIndex = questions.findIndex(q => q.id === questionId);
      const updatedQuestions = [
        ...questions.slice(0, sourceIndex + 1),
        duplicatedQuestion,
        ...questions.slice(sourceIndex + 1)
      ];

      setQuestions(updatedQuestions);
      setHasChanges(true);
      onQuestionsUpdated(updatedQuestions, undefined, undefined, blocks);

      // Open the duplicate in edit mode for immediate customization
      setOriginalQuestionBeforeEdit(duplicatedQuestion);
      setEditingQuestionId(duplicatedQuestion.id);
    } catch (error) {
      console.error('Failed to duplicate question:', error);
    }
  };

  const getBlockLabel = (blockId: string) =>
    blocks.find((b) => b.id === blockId)?.label ?? blockId;

  const duplicateBlock = (blockId: string) => {
    const meta = blocks.find((b) => b.id === blockId);
    const indices = questions
      .map((q, i) => (q.block_id === blockId ? i : -1))
      .filter((i) => i >= 0);
    if (indices.length === 0) return;

    const newBlockId = `block_${crypto.randomUUID()}`;
    const newMeta: SurveyBlockDefinition = {
      id: newBlockId,
      label: `${meta?.label ?? 'Block'} copy`,
      randomize_within: meta?.randomize_within ?? false,
    };
    const lastIdx = Math.max(...indices);

    const dupQs: ParsedQuestion[] = indices.map((i) => {
      const sq = questions[i];
      const dq: ParsedQuestion = {
        ...structuredClone(sq),
        id: `question_${crypto.randomUUID()}`,
        block_id: newBlockId,
      };
      if (dq.type === 'image_hotspot' && dq.bounding_boxes) {
        dq.bounding_boxes = dq.bounding_boxes.map((box) => ({
          ...box,
          id: `box_${crypto.randomUUID()}`,
        }));
      }
      return dq;
    });

    const nextBlocks = [...blocks, newMeta];
    const newQuestions = [...questions.slice(0, lastIdx + 1), ...dupQs, ...questions.slice(lastIdx + 1)];

    setBlocks(nextBlocks);
    setQuestions(newQuestions);
    setHasChanges(true);
    onQuestionsUpdated(newQuestions, undefined, undefined, nextBlocks);
  };

  const updateBlockRandomizeWithin = (blockId: string, value: boolean) => {
    const next = blocks.map((b) => (b.id === blockId ? { ...b, randomize_within: value } : b));
    setBlocks(next);
    setHasChanges(true);
    onQuestionsUpdated(questions, undefined, undefined, next);
  };

  const updateBlockDisplayCondition = (blockId: string, displayCondition: SurveyBlockDefinition['display_condition'] | undefined) => {
    const next = blocks.map((b) => (b.id === blockId ? { ...b, display_condition: displayCondition } : b));
    setBlocks(next);
    setHasChanges(true);
    onQuestionsUpdated(questions, undefined, undefined, next);
  };

  const updateBlockLabel = (blockId: string, newLabel: string) => {
    const next = blocks.map((b) => (b.id === blockId ? { ...b, label: newLabel } : b));
    setBlocks(next);
    setHasChanges(true);
    onQuestionsUpdated(questions, undefined, undefined, next);
  };

  const addChoice = (questionId: string) => {
    const question = questions.find(q => q.id === questionId);
    if (question) {
      const choices = question.choices || [];
      updateQuestion(questionId, {
        choices: [...choices, `Option ${choices.length + 1}`]
      });
    }
  };

  const removeChoice = (questionId: string, choiceIndex: number) => {
    const question = questions.find(q => q.id === questionId);
    if (question && question.choices) {
      const choices = question.choices.filter((_, index) => index !== choiceIndex);
      // Also remove the corresponding media URL if it exists
      const mediaUrls = question.media_urls
        ? question.media_urls.filter((_, index) => index !== choiceIndex)
        : undefined;
      
      // Remove the corresponding choice_action at the same index
      const choiceActions = question.choice_actions
        ? question.choice_actions.filter((_, index) => index !== choiceIndex)
        : undefined;
      
      // Re-index option_media keys (shift indices down for entries after removed index)
      let optionMedia: { [optionIndex: number]: string[] } | undefined = undefined;
      if (question.option_media) {
        optionMedia = {};
        Object.entries(question.option_media).forEach(([key, urls]) => {
          const oldIndex = parseInt(key);
          if (oldIndex < choiceIndex) {
            // Keep indices before removed choice unchanged
            optionMedia![oldIndex] = urls;
          } else if (oldIndex > choiceIndex) {
            // Shift indices after removed choice down by 1
            optionMedia![oldIndex - 1] = urls;
          }
          // Skip the removed choice index entirely
        });
        // Clean up if empty
        if (Object.keys(optionMedia).length === 0) {
          optionMedia = undefined;
        }
      }
      
      updateQuestion(questionId, {
        choices,
        media_urls: mediaUrls && mediaUrls.length > 0 ? mediaUrls : undefined,
        choice_actions: choiceActions && choiceActions.length > 0 ? choiceActions : undefined,
        option_media: optionMedia
      });
    }
  };

  const updateChoice = (questionId: string, choiceIndex: number, value: string) => {
    const question = questions.find(q => q.id === questionId);
    if (question && question.choices) {
      const choices = [...question.choices];
      choices[choiceIndex] = value;
      
      // Also update the value field in choice_actions to keep them in sync
      // (backend matches skip logic by value field)
      let choiceActions = question.choice_actions;
      if (choiceActions && choiceActions[choiceIndex]) {
        choiceActions = [...choiceActions];
        choiceActions[choiceIndex] = {
          ...choiceActions[choiceIndex],
          value: value
        };
      }
      
      updateQuestion(questionId, { 
        choices,
        ...(choiceActions ? { choice_actions: choiceActions } : {})
      });
    }
  };

  // Helper functions for question-level media management
  // Using functional state updates to handle rapid successive calls properly
  const addQuestionMedia = (questionId: string, url: string) => {
    setQuestions(prevQuestions =>
      prevQuestions.map((q) => {
        if (q.id !== questionId) return q;
        return { ...q, media_urls: [...(q.media_urls || []), url] };
      })
    );
    setHasChanges(true);
  };

  const removeQuestionMedia = (questionId: string, index: number) => {
    setQuestions(prevQuestions =>
      prevQuestions.map((q) => {
        if (q.id !== questionId || !q.media_urls) return q;
        const next = q.media_urls.filter((_, i) => i !== index);
        const updates: Partial<ParsedQuestion> = {
          media_urls: next.length > 0 ? next : undefined,
        };
        if (next.length < 2) {
          updates.randomize_media_order = false;
        }
        return { ...q, ...updates };
      })
    );
    setHasChanges(true);
  };

  const replaceQuestionMedia = (questionId: string, index: number, url: string) => {
    setQuestions(prevQuestions =>
      prevQuestions.map(q =>
        q.id === questionId && q.media_urls
          ? { ...q, media_urls: q.media_urls.map((u, i) => (i === index ? url : u)) }
          : q
      )
    );
    setHasChanges(true);
  };

  const reorderQuestionMedia = (questionId: string, fromIndex: number, toIndex: number) => {
    setQuestions(prevQuestions =>
      prevQuestions.map(q => {
        if (q.id !== questionId || !q.media_urls) return q;
        const urls = [...q.media_urls];
        const [moved] = urls.splice(fromIndex, 1);
        urls.splice(toIndex, 0, moved);
        return { ...q, media_urls: urls };
      })
    );
    setHasChanges(true);
  };

  // Helper functions for option-level media management
  const addOptionMedia = (questionId: string, optionIndex: number, url: string) => {
    setQuestions(prevQuestions =>
      prevQuestions.map(q => {
        if (q.id !== questionId) return q;
        const optionMedia = { ...(q.option_media || {}) };
        optionMedia[optionIndex] = [...(optionMedia[optionIndex] || []), url];
        return { ...q, option_media: optionMedia };
      })
    );
    setHasChanges(true);
  };

  const removeOptionMedia = (questionId: string, optionIndex: number, mediaIndex: number) => {
    setQuestions(prevQuestions =>
      prevQuestions.map(q => {
        if (q.id !== questionId || !q.option_media || !q.option_media[optionIndex]) return q;
        const optionMedia = { ...(q.option_media || {}) };
        optionMedia[optionIndex] = optionMedia[optionIndex].filter((_, i) => i !== mediaIndex);
        // Clean up empty arrays
        if (optionMedia[optionIndex].length === 0) {
          delete optionMedia[optionIndex];
        }
        return { ...q, option_media: optionMedia };
      })
    );
    setHasChanges(true);
  };

  const replaceOptionMedia = (questionId: string, optionIndex: number, mediaIndex: number, url: string) => {
    setQuestions(prevQuestions =>
      prevQuestions.map(q => {
        if (q.id !== questionId || !q.option_media) return q;
        const om = q.option_media;
        const existing = om[optionIndex] ?? (om as Record<string, string[] | undefined>)[String(optionIndex)];
        if (!existing || mediaIndex < 0 || mediaIndex >= existing.length) return q;
        const optionMedia = { ...om };
        optionMedia[optionIndex] = existing.map((u, i) => (i === mediaIndex ? url : u));
        return { ...q, option_media: optionMedia };
      })
    );
    setHasChanges(true);
  };

  const reorderOptionMedia = (questionId: string, optionIndex: number, fromIndex: number, toIndex: number) => {
    setQuestions(prevQuestions =>
      prevQuestions.map(q => {
        if (q.id !== questionId || !q.option_media) return q;
        const om = q.option_media;
        const existing = om[optionIndex] ?? (om as Record<string, string[] | undefined>)[String(optionIndex)];
        if (!existing) return q;
        const urls = [...existing];
        const [moved] = urls.splice(fromIndex, 1);
        urls.splice(toIndex, 0, moved);
        const optionMedia = { ...om };
        optionMedia[optionIndex] = urls;
        return { ...q, option_media: optionMedia };
      })
    );
    setHasChanges(true);
  };

  const getOptionMediaUrls = (question: ParsedQuestion, optionIndex: number): string[] => {
    const om = question.option_media;
    if (!om) return [];
    const byNum = om[optionIndex];
    if (byNum) return byNum;
    const asRecord = om as Record<string, string[] | undefined>;
    return asRecord[String(optionIndex)] ?? [];
  };

  // Helper functions for managing choice actions (screener/skip logic)
  const updateChoiceAction = (
    questionId: string,
    choiceIndex: number,
    action?: 'terminate' | 'skip_to',
    target?: string,
    reason?: string
  ) => {
    const question = questions.find(q => q.id === questionId);
    if (!question || !question.choices) return;

    // Get or create choice_actions array matching choices length
    const choiceActions: ChoiceAction[] = question.choices.map((choice, idx) => {
      const existingAction = question.choice_actions?.[idx];

      if (idx === choiceIndex) {
        // Update this choice's action
        return {
          value: choice,
          action,
          target: action === 'skip_to' ? target : undefined,
          reason: action === 'terminate' ? reason : undefined
        };
      }

      // Preserve existing action or create default
      return existingAction || { value: choice };
    });

    updateQuestion(questionId, { choice_actions: choiceActions });
  };

  // Get action metadata for a specific choice
  const getChoiceAction = (question: ParsedQuestion, choiceIndex: number): ChoiceAction | undefined => {
    return question.choice_actions?.[choiceIndex];
  };

  const addMatrixItem = (questionId: string) => {
    const question = questions.find(q => q.id === questionId);
    if (question) {
      const items = question.matrix_rows || [];
      updateQuestion(questionId, {
        matrix_rows: [...items, `Item ${items.length + 1}`]
      });
    }
  };

  const removeMatrixItem = (questionId: string, itemIndex: number) => {
    const question = questions.find(q => q.id === questionId);
    if (question && question.matrix_rows) {
      const items = question.matrix_rows.filter((_, index) => index !== itemIndex);
      updateQuestion(questionId, { matrix_rows: items });
    }
  };

  const updateMatrixItem = (questionId: string, itemIndex: number, value: string) => {
    const question = questions.find(q => q.id === questionId);
    if (question && question.matrix_rows) {
      const items = [...question.matrix_rows];
      items[itemIndex] = value;
      updateQuestion(questionId, { matrix_rows: items });
    }
  };

  const addConjointAttribute = (questionId: string) => {
    const question = questions.find(q => q.id === questionId);
    if (question) {
      const attributes = question.conjoint_attributes || [];
      updateQuestion(questionId, {
        conjoint_attributes: [...attributes, { name: `Attribute ${attributes.length + 1}`, levels: ['Level 1', 'Level 2'] }]
      });
    }
  };

  const removeConjointAttribute = (questionId: string, attrIndex: number) => {
    const question = questions.find(q => q.id === questionId);
    if (question && question.conjoint_attributes) {
      const attributes = question.conjoint_attributes.filter((_, index) => index !== attrIndex);
      updateQuestion(questionId, { conjoint_attributes: attributes });
    }
  };

  const updateConjointAttribute = (questionId: string, attrIndex: number, field: string, value: any) => {
    const question = questions.find(q => q.id === questionId);
    if (question && question.conjoint_attributes) {
      const attributes = [...question.conjoint_attributes];
      attributes[attrIndex] = { ...attributes[attrIndex], [field]: value };
      updateQuestion(questionId, { conjoint_attributes: attributes });
    }
  };

  const getQuestionTypeLabel = (type: string) => {
    const questionType = QUESTION_TYPES.find(qt => qt.value === type);
    return questionType ? questionType.label : type;
  };

  const hasChoices = (type: string) => {
    return CHOICE_BASED_TYPES.includes(type);
  };

  const hasScale = (type: string) => {
    return SCALE_BASED_TYPES.includes(type);
  };

  const hasRanking = (type: string) => {
    return RANKING_BASED_TYPES.includes(type);
  };

  const hasMatrix = (type: string) => {
    return MATRIX_BASED_TYPES.includes(type);
  };

  const hasMaxDiff = (type: string) => {
    return MAX_DIFF_BASED_TYPES.includes(type);
  };

  const hasConjoint = (type: string) => {
    return CONJOINT_BASED_TYPES.includes(type);
  };

  const hasImageHotspot = (type: string) => {
    return IMAGE_HOTSPOT_TYPES.includes(type);
  };

  const hasAllocation = (type: string) => {
    return ALLOCATION_BASED_TYPES.includes(type);
  };

  const hasPhotoUpload = (type: string) => {
    return PHOTO_UPLOAD_TYPES.includes(type);
  };

  // Helper function to parse comma-separated key=value pairs into object
  const parseParams = (paramsString: string): { [key: string]: string } => {
    if (!paramsString.trim()) return {};

    const params: { [key: string]: string } = {};
    const pairs = paramsString.split(',').map(p => p.trim()).filter(p => p);

    for (const pair of pairs) {
      const [key, value] = pair.split('=').map(s => s.trim());
      if (key && value) {
        params[key] = value;
      }
    }

    return params;
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, index: number) => {
    // Don't allow dragging while editing or in readonly mode
    if (editingQuestionId || readOnly) {
      e.preventDefault();
      return;
    }

    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    // Set drag image opacity
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '0.5';
    }
  };

  const handleDragEnd = (e: React.DragEvent) => {
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '1';
    }
    setDraggedIndex(null);
    setDropTargetIndex(null);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    setDropTargetIndex(index);
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragLeave = () => {
    setDropTargetIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDropTargetIndex(null);
      return;
    }

    // Reorder the questions array
    const reorderedQuestions = Array.from(questions);
    const [draggedQuestion] = reorderedQuestions.splice(draggedIndex, 1);
    reorderedQuestions.splice(dropIndex, 0, draggedQuestion);

    setQuestions(reorderedQuestions);
    setHasChanges(true);
    onQuestionsUpdated(reorderedQuestions, undefined, undefined, blocks);

    // Reset drag state
    setDraggedIndex(null);
    setDropTargetIndex(null);
  };

  const handleCreateSurvey = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      // Build redirect config if any fields are filled
      let redirectConfig: RedirectConfig | null = null;

      if (showRedirectConfig && redirectBaseUrl.trim()) {
        // Parse parameters
        const completionParamsObj = parseParams(completionParams);
        const screenOutParamsObj = parseParams(screenOutParams);
        const quotaFullParamsObj = parseParams(quotaFullParams);

        // Build tracking param
        const trackingParam: { [key: string]: string } = {};
        if (trackingParamName.trim()) {
          trackingParam[trackingParamName.trim()] = '{TRACKING_ID}';
        }

        redirectConfig = {
          redirect_base_url: redirectBaseUrl.trim(),
          screened_out_url: screenedOutUrl.trim() || null,
          tracking_param: trackingParam,
          completion_params: Object.keys(completionParamsObj).length > 0 ? completionParamsObj : null,
          screen_out_params: Object.keys(screenOutParamsObj).length > 0 ? screenOutParamsObj : null,
          quota_full_params: Object.keys(quotaFullParamsObj).length > 0 ? quotaFullParamsObj : null,
        };
      }

      // Parse maxResponses - convert to number or null
      const maxResponsesValue = maxResponses.trim() ? parseInt(maxResponses) : null;

      await onCreateSurvey(surveyName, questions, redirectConfig, maxResponsesValue, blocks);
    } catch (error) {
      console.error('Failed to create survey:', error);
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className={`bg-white rounded-xl shadow-sm p-6 ${className}`}>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mr-3"></div>
          <span className="text-gray-600">{UI_TEXT.LOADING.PROCESSING}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl shadow-sm p-6 ${className}`}>
      {/* Header - always shown for navigation */}
      <div className="mb-6">
        {!readOnly && !hideActionButtons && (
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={onGoBack}
              icon={<ArrowLeft className="w-4 h-4 mr-1" />}
            >
              {UI_TEXT.BUTTONS.BACK}
            </Button>
            {hasChanges && (
              <div className="flex items-center text-amber-600 text-sm">
                <AlertCircle className="w-4 h-4 mr-1" />
                {UI_TEXT.AI_SURVEY.QUESTIONS_PREVIEW.DESCRIPTIONS.UNSAVED_CHANGES}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center mb-2">
          <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
          <h2 className="text-xl font-semibold text-gray-900">
            {readOnly ? 'Questions' : hideActionButtons ? 'Edit Survey' : UI_TEXT.AI_SURVEY.QUESTIONS_PREVIEW.TITLES.REVIEW_QUESTIONS}
          </h2>
        </div>
        <p className="text-gray-600">
          {readOnly
            ? ''
            : hideActionButtons
              ? 'Edit questions, response quota, and panel provider settings for this survey.'
              : 'Review questions, configure quota, and set up panel provider.'}
        </p>
      </div>

      {/* Questions List */}
      <div className="mb-6">
        {/* Blocks are created inline via the Block dropdown on each question */}
        <div className="space-y-2 mb-4">
          {questions.map((question, index) => (
            <React.Fragment key={question.id}>
              {/* Insert Question Button - appears on hover between questions */}
              {!readOnly && editingQuestionId === null && (
                <div className="group relative h-4 -my-1">
                  <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="flex-1 h-px bg-gray-300" />
                    <button
                      onClick={() => insertQuestionAt(index)}
                      className="mx-2 flex items-center gap-1 px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium rounded-full shadow-sm transition-all hover:shadow-md"
                    >
                      <Plus className="w-3 h-3" />
                      Insert Question
                    </button>
                    <div className="flex-1 h-px bg-gray-300" />
                  </div>
                </div>
              )}

              {(() => {
                const bid = question.block_id ?? null;
                const prevBid = index > 0 ? questions[index - 1].block_id ?? null : null;
                const showHeader = Boolean(bid && bid !== prevBid);
                if (!showHeader || !bid) return null;
                const bmeta = blocks.find((b) => b.id === bid);
                return (
                  <>
                  <div className="flex flex-wrap items-center gap-3 py-2 px-3 bg-slate-100 rounded-lg border border-slate-200 mb-2">
                    {editingBlockId === bid ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={bmeta?.label ?? ''}
                          onChange={(e) => updateBlockLabel(bid, e.target.value)}
                          className="text-sm font-semibold text-slate-800 px-2 py-1 border border-slate-300 rounded-md w-40"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              setEditingBlockId(null);
                            } else if (e.key === 'Escape') {
                              setEditingBlockId(null);
                            }
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => setEditingBlockId(null)}
                          className="text-xs text-slate-600 hover:text-slate-800 px-2 py-1 rounded hover:bg-slate-200"
                        >
                          Done
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => !readOnly && setEditingBlockId(bid)}
                        className={`text-sm font-semibold text-slate-800 flex items-center gap-1.5 ${!readOnly ? 'hover:text-slate-600 cursor-pointer' : ''}`}
                        disabled={readOnly}
                      >
                        {getBlockLabel(bid)}
                        {!readOnly && <Edit3 className="w-3 h-3 text-slate-400" />}
                      </button>
                    )}
                    {!readOnly && (
                      <>
                        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300"
                            checked={bmeta?.randomize_within ?? false}
                            onChange={(e) => updateBlockRandomizeWithin(bid, e.target.checked)}
                          />
                          Randomize within block
                        </label>
                        <Button type="button" variant="outline" size="sm" onClick={() => duplicateBlock(bid)}>
                          Duplicate Block
                        </Button>
                        <Button
                          type="button"
                          variant={bmeta?.display_condition ? 'outline' : 'ghost'}
                          size="sm"
                          onClick={() => setEditingBlockConditionId(editingBlockConditionId === bid ? null : bid)}
                          className={bmeta?.display_condition ? 'border-amber-300 text-amber-700 hover:bg-amber-50' : ''}
                        >
                          {bmeta?.display_condition ? 'Edit Condition' : 'Add Condition'}
                        </Button>
                      </>
                    )}
                    {readOnly && bmeta?.randomize_within ? (
                      <span className="text-xs text-slate-600">Shuffles question order within this block</span>
                    ) : null}
                    {readOnly && bmeta?.display_condition && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                        Conditional
                      </span>
                    )}
                  </div>
                  {/* Block Condition Editor */}
                  {!readOnly && editingBlockConditionId === bid && (() => {
                    // Find index of first question in this block to determine eligible questions
                    const firstQInBlockIdx = questions.findIndex(q => q.block_id === bid);
                    const eligibleQuestions = firstQInBlockIdx > 0
                      ? questions.slice(0, firstQInBlockIdx).filter(q => DISPLAY_CONDITION_TYPES.includes(q.type))
                      : [];

                    const dc = bmeta?.display_condition;
                    const conditions = dc?.conditions || [];
                    const logic = dc?.logic || 'and';

                    const updateBlockConditions = (newConditions: any[], newLogic?: string) => {
                      if (newConditions.length === 0) {
                        updateBlockDisplayCondition(bid, undefined);
                      } else {
                        updateBlockDisplayCondition(bid, { logic: (newLogic || logic) as 'and' | 'or', conditions: newConditions });
                      }
                    };

                    return (
                      <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-amber-800">Block Display Condition</span>
                          <button
                            type="button"
                            onClick={() => setEditingBlockConditionId(null)}
                            className="text-amber-600 hover:text-amber-800"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>

                        {eligibleQuestions.length === 0 ? (
                          <p className="text-sm text-amber-700">
                            No eligible questions before this block. Add choice or percentage allocation questions before the block to enable conditions.
                          </p>
                        ) : (
                          <div className="space-y-2">
                            {conditions.length > 1 && (
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-xs text-amber-700">Logic:</span>
                                <select
                                  value={logic}
                                  onChange={(e) => updateBlockConditions(conditions, e.target.value)}
                                  className="text-xs border border-amber-300 rounded px-2 py-1"
                                >
                                  <option value="and">AND (all must match)</option>
                                  <option value="or">OR (any must match)</option>
                                </select>
                              </div>
                            )}

                            {conditions.map((cond: any, condIdx: number) => {
                              const sourceQ = questions.find(q => q.id === cond.source_question_id);
                              const isPct = sourceQ?.type === 'percentage_allocation';
                              return (
                                <div key={condIdx} className="bg-white border border-amber-200 rounded p-2 space-y-2">
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs text-amber-600">Condition {condIdx + 1}</span>
                                    <button
                                      type="button"
                                      onClick={() => updateBlockConditions(conditions.filter((_, i) => i !== condIdx))}
                                      className="text-amber-500 hover:text-amber-700"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  </div>
                                  <div className="flex flex-wrap gap-2 items-center">
                                    <select
                                      value={cond.source_question_id}
                                      onChange={(e) => {
                                        const newSrc = questions.find(q => q.id === e.target.value);
                                        const newIsPct = newSrc?.type === 'percentage_allocation';
                                        const updated = newIsPct
                                          ? { source_question_id: e.target.value, operator: '>', option_key: newSrc?.allocation_options?.[0] || newSrc?.choices?.[0] || '', threshold: 0, value: '' }
                                          : { source_question_id: e.target.value, operator: 'selected', value: newSrc?.choices?.[0] || '' };
                                        updateBlockConditions(conditions.map((c, i) => i === condIdx ? updated : c));
                                      }}
                                      className="text-xs border border-gray-300 rounded px-2 py-1"
                                    >
                                      {eligibleQuestions.map((eq) => (
                                        <option key={eq.id} value={eq.id}>
                                          Q{questions.indexOf(eq) + 1}: {eq.text.slice(0, 30)}...
                                        </option>
                                      ))}
                                    </select>
                                    {isPct ? (
                                      <>
                                        <select
                                          value={cond.option_key || ''}
                                          onChange={(e) => updateBlockConditions(conditions.map((c, i) => i === condIdx ? { ...c, option_key: e.target.value } : c))}
                                          className="text-xs border border-gray-300 rounded px-2 py-1"
                                        >
                                          {(sourceQ?.allocation_options || sourceQ?.choices || []).map((opt: string) => (
                                            <option key={opt} value={opt}>{opt}</option>
                                          ))}
                                        </select>
                                        <select
                                          value={cond.operator}
                                          onChange={(e) => updateBlockConditions(conditions.map((c, i) => i === condIdx ? { ...c, operator: e.target.value } : c))}
                                          className="text-xs border border-gray-300 rounded px-2 py-1"
                                        >
                                          <option value=">">&gt;</option>
                                          <option value=">=">&ge;</option>
                                          <option value="=">=</option>
                                          <option value="<">&lt;</option>
                                          <option value="<=">&le;</option>
                                        </select>
                                        <input
                                          type="number"
                                          value={cond.threshold ?? 0}
                                          onChange={(e) => updateBlockConditions(conditions.map((c, i) => i === condIdx ? { ...c, threshold: parseInt(e.target.value) || 0 } : c))}
                                          className="w-16 text-xs border border-gray-300 rounded px-2 py-1"
                                        />
                                      </>
                                    ) : (
                                      <>
                                        <select
                                          value={cond.operator}
                                          onChange={(e) => updateBlockConditions(conditions.map((c, i) => i === condIdx ? { ...c, operator: e.target.value } : c))}
                                          className="text-xs border border-gray-300 rounded px-2 py-1"
                                        >
                                          <option value="selected">selected</option>
                                          <option value="not_selected">not selected</option>
                                        </select>
                                        <select
                                          value={cond.value}
                                          onChange={(e) => updateBlockConditions(conditions.map((c, i) => i === condIdx ? { ...c, value: e.target.value } : c))}
                                          className="text-xs border border-gray-300 rounded px-2 py-1"
                                        >
                                          {(sourceQ?.choices || []).map((ch: string) => (
                                            <option key={ch} value={ch}>{ch}</option>
                                          ))}
                                        </select>
                                      </>
                                    )}
                                  </div>
                                </div>
                              );
                            })}

                            <button
                              type="button"
                              onClick={() => {
                                const sq = eligibleQuestions[eligibleQuestions.length - 1];
                                const isPct = sq.type === 'percentage_allocation';
                                const newCond = isPct
                                  ? { source_question_id: sq.id, operator: '>', option_key: sq.allocation_options?.[0] || sq.choices?.[0] || '', threshold: 0, value: '' }
                                  : { source_question_id: sq.id, operator: 'selected', value: sq.choices?.[0] || '' };
                                updateBlockConditions([...conditions, newCond]);
                              }}
                              className="text-xs text-amber-700 hover:text-amber-900"
                            >
                              + Add condition
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {/* Block Condition Summary - show when condition exists but not editing */}
                  {!readOnly && editingBlockConditionId !== bid && bmeta?.display_condition && (() => {
                    const dc = bmeta.display_condition;
                    const conditions = dc.conditions || [];
                    const logic = dc.logic || 'and';

                    const summaries = conditions.map((cond: any) => {
                      const sourceQ = questions.find(q => q.id === cond.source_question_id);
                      const sourceIdx = sourceQ ? questions.indexOf(sourceQ) + 1 : '?';
                      if (sourceQ?.type === 'percentage_allocation') {
                        const opDisplay: Record<string, string> = { '>': '>', '>=': '\u2265', '=': '=', '<': '<', '<=': '\u2264' };
                        return `Q${sourceIdx} "${cond.option_key}" ${opDisplay[cond.operator] || cond.operator} ${cond.threshold}`;
                      }
                      return `Q${sourceIdx} "${cond.value}" ${cond.operator === 'selected' ? 'selected' : 'not selected'}`;
                    });

                    return (
                      <div className="mt-1 text-xs text-amber-700">
                        Show block when: {summaries.join(` ${logic.toUpperCase()} `)}
                      </div>
                    );
                  })()}
                  </>
                );
              })()}

              <div
                draggable={!editingQuestionId && !readOnly}
                onDragStart={(e) => handleDragStart(e, index)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
                className={`border rounded-lg p-4 transition-colors ${blockAccentClass(question.block_id)} ${
                  draggedIndex === index
                    ? 'border-blue-500 bg-blue-50 opacity-50'
                    : dropTargetIndex === index
                    ? 'border-blue-400 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
            {/* Question Header */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center mb-2 flex-wrap gap-y-1">
                  <span className="text-sm font-medium text-gray-500 mr-3">
                    Q{index + 1}
                  </span>
                  {question.randomization_group && (
                    <span
                      className={`mr-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${randomizationPoolBadgeClass(
                        question.randomization_group,
                        randomizationPoolsInSurvey
                      )}`}
                      title={`Randomization pool: ${question.randomization_group}`}
                    >
                      {formatRandomizationPoolLabel(question.randomization_group)}
                    </span>
                  )}
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {getQuestionTypeLabel(question.type)}
                  </span>
                  {question.block_id ? (
                    <span className="mr-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-200 text-slate-800 border border-slate-300">
                      {getBlockLabel(question.block_id)}
                    </span>
                  ) : null}
                  {groupCount > 1 && editingQuestionId !== question.id && (
                    <div className="ml-2 flex items-center gap-1">
                      {Array.from({ length: groupCount }, (_, gi) => {
                        const active = !question.visible_groups || question.visible_groups.includes(gi);
                        const color = getGroupColor(gi);
                        return (
                          <span
                            key={gi}
                            title={`${getGroupLabel(gi)}${active ? '' : ' (hidden)'}`}
                            className={`w-5 h-5 rounded flex items-center justify-center text-[9px] font-bold ${
                              active
                                ? `${color.header} text-white`
                                : 'bg-gray-200 text-gray-400'
                            }`}
                          >
                            {String.fromCharCode(65 + gi)}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>

                {editingQuestionId === question.id ? (
                  <div className="space-y-3">
                    <div className="flex items-start gap-2">
                      <textarea
                        value={question.text}
                        onChange={(e) => updateQuestion(question.id, { text: e.target.value })}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md resize-y min-h-[60px]"
                        rows={3}
                        placeholder={UI_TEXT.AI_SURVEY.QUESTIONS_PREVIEW.LABELS.QUESTION_TEXT_PLACEHOLDER}
                      />
                      {/* Question-level Media Upload Icon */}
                      {MEDIA_SUPPORTED_TYPES.includes(question.type) && (() => {
                        const groupMediaCount = question.group_media ? Object.values(question.group_media).reduce((s, arr) => s + arr.length, 0) : 0;
                        const totalMedia = (question.media_urls?.length || 0) + groupMediaCount;
                        return (
                          <MediaUploadIcon
                            hasMedia={totalMedia > 0}
                            mediaCount={totalMedia}
                            onClick={() => setExpandedQuestionMedia(
                              expandedQuestionMedia === question.id ? null : question.id
                            )}
                          />
                        );
                      })()}
                    </div>

                    {/* Description field for info_screen questions */}
                    {question.type === 'info_screen' && (
                      <div className="mt-3">
                        <label className="block text-xs text-gray-600 mb-1">
                          Additional Description (shown as gray text below main content)
                        </label>
                        <textarea
                          value={question.description || ''}
                          onChange={(e) => updateQuestion(question.id, { description: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none"
                          rows={3}
                          placeholder="Optional additional context shown to respondents as gray text"
                        />
                      </div>
                    )}

                    {/* Question-level Media Grid - show if has media OR is expanded */}
                    {MEDIA_SUPPORTED_TYPES.includes(question.type) && 
                     ((question.media_urls?.length || 0) > 0 || (question.group_media && Object.keys(question.group_media).length > 0) || expandedQuestionMedia === question.id) && (
                      <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="text-xs font-medium text-gray-600 mb-2">Question Media</div>
                        {groupCount > 1 ? (
                          <GroupMediaUpload
                            groupCount={groupCount}
                            visibleGroups={question.visible_groups}
                            groupMedia={question.group_media || {}}
                            onMediaAdd={(gi, url) => {
                              setQuestions(prev => prev.map(q => {
                                if (q.id !== question.id) return q;
                                const gm = { ...(q.group_media || {}) };
                                gm[String(gi)] = [...(gm[String(gi)] || []), url];
                                return { ...q, group_media: gm };
                              }));
                              setHasChanges(true);
                            }}
                            onMediaRemove={(gi, removeIdx) => {
                              setQuestions(prev => prev.map(q => {
                                if (q.id !== question.id) return q;
                                const gm = { ...(q.group_media || {}) };
                                const arr = (gm[String(gi)] || []).filter((_, i) => i !== removeIdx);
                                if (arr.length > 0) gm[String(gi)] = arr;
                                else delete gm[String(gi)];
                                return { ...q, group_media: Object.keys(gm).length > 0 ? gm : undefined };
                              }));
                              setHasChanges(true);
                            }}
                            onMediaReplace={(gi, mediaIdx, newUrl) => {
                              setQuestions(prev => prev.map(q => {
                                if (q.id !== question.id) return q;
                                const gm = { ...(q.group_media || {}) };
                                const arr = [...(gm[String(gi)] || [])];
                                if (mediaIdx < 0 || mediaIdx >= arr.length) return q;
                                arr[mediaIdx] = newUrl;
                                gm[String(gi)] = arr;
                                return { ...q, group_media: gm };
                              }));
                              setHasChanges(true);
                            }}
                            maxItemsPerGroup={5}
                          />
                        ) : (
                          <MediaUploadGrid
                            mediaUrls={question.media_urls || []}
                            onMediaAdd={(url) => addQuestionMedia(question.id, url)}
                            onMediaRemove={(index) => removeQuestionMedia(question.id, index)}
                            onMediaReplace={(index, url) => replaceQuestionMedia(question.id, index, url)}
                            onMediaReorder={(from, to) => reorderQuestionMedia(question.id, from, to)}
                            maxItems={5}
                          />
                        )}
                        {groupCount === 1 && (question.media_urls?.length || 0) >= 2 && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <label className="flex items-center gap-2 cursor-pointer select-none">
                              <input
                                type="checkbox"
                                checked={shouldRandomizeMedia(question)}
                                onChange={(e) => updateQuestion(question.id, {
                                  randomize_media_order: e.target.checked,
                                  image_position_mode: null,
                                  image_order: null,
                                })}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                              />
                              <span className="text-xs font-medium text-gray-600">
                                Randomize image/video order per respondent
                              </span>
                            </label>
                            {shouldRandomizeMedia(question) && (
                              <p className="text-xs text-gray-500 mt-1 ml-6">
                                Each respondent will see media in a different random order. Drag-and-drop above to set the default order.
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    <select
                      value={question.type}
                      onChange={(e) => updateQuestion(question.id, { type: e.target.value })}
                      className="px-3 py-2 border border-gray-300 rounded-md"
                    >
                      {QUESTION_TYPES.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>

                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-medium text-gray-500">Block</span>
                      <select
                        value={question.block_id ?? ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === '__new__') {
                            const id = `block_${Date.now()}`;
                            const label = `Block ${blocks.length + 1}`;
                            setBlocks([...blocks, { id, label, randomize_within: false }]);
                            updateQuestion(question.id, { block_id: id });
                            setHasChanges(true);
                          } else {
                            updateQuestion(question.id, { block_id: val === '' ? null : val });
                          }
                        }}
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm min-w-[10rem]"
                      >
                        <option value="">None</option>
                        {blocks.map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.label}
                          </option>
                        ))}
                        <option value="__new__">+ New Block</option>
                      </select>
                    </div>

                    {/* Visible to Groups — compact toggle row */}
                    {groupCount > 1 && (
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-medium text-gray-500 mr-1">Groups:</span>
                        {Array.from({ length: groupCount }, (_, gi) => {
                          const selected = !question.visible_groups || question.visible_groups.includes(gi);
                          const color = getGroupColor(gi);
                          return (
                            <button
                              key={gi}
                              type="button"
                              onClick={() => {
                                const current = question.visible_groups || Array.from({ length: groupCount }, (_, i) => i);
                                const next = selected
                                  ? current.filter(g => g !== gi)
                                  : [...current, gi].sort();
                                updateQuestion(question.id, {
                                  visible_groups: next.length === groupCount ? undefined : next,
                                });
                              }}
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium transition-all ${
                                selected
                                  ? `${color.header} text-white shadow-sm`
                                  : 'bg-gray-100 text-gray-400 line-through'
                              }`}
                            >
                              {getGroupLabel(gi)}
                            </button>
                          );
                        })}
                      </div>
                    )}

                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-start gap-2">
                      <QuestionText className="text-gray-900 font-medium flex-1">{question.text}</QuestionText>
                      {/* Show media count badge in read mode */}
                      {MEDIA_SUPPORTED_TYPES.includes(question.type) && ((question.media_urls?.length || 0) > 0 || (question.group_media && Object.keys(question.group_media).length > 0)) && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-800">
                          {question.group_media
                            ? `${Object.values(question.group_media).reduce((s, arr) => s + arr.length, 0)} media`
                            : `${question.media_urls?.length} media`
                          }
                        </span>
                      )}
                    </div>
                    {/* Show description for info_screen in read mode */}
                    {question.type === 'info_screen' && question.description && (
                      <div className="mt-2 pt-2 border-t border-gray-100">
                        <p className="text-gray-500 text-sm leading-relaxed">
                          {question.description}
                        </p>
                      </div>
                    )}
                    {/* Show media previews in read mode */}
                    {MEDIA_SUPPORTED_TYPES.includes(question.type) && (question.media_urls?.length || 0) > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {(question.media_urls || []).map((url, idx) => {
                          const fullUrl = url.startsWith('/api/')
                            ? `${import.meta.env.VITE_API_URL || ''}${url}`
                            : url;
                          const mediaType = getMediaTypeFromUrl(url);
                          return (
                            <div key={idx} className="w-20 h-20 rounded-lg border border-gray-200 overflow-hidden bg-gray-50">
                              {mediaType === 'video' ? (
                                renderVideoThumb(url, fullUrl)
                              ) : (
                                <img
                                  src={fullUrl}
                                  alt={`Question media ${idx + 1}`}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = 'none';
                                  }}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {/* Show group media previews in read mode */}
                    {groupCount > 1 && question.group_media && Object.keys(question.group_media).length > 0 && (
                      <div className="space-y-2">
                        {(question.visible_groups ?? Array.from({ length: groupCount }, (_, i) => i)).map((gi) => {
                          const urls = question.group_media?.[String(gi)];
                          if (!urls || urls.length === 0) return null;
                          const color = getGroupColor(gi);
                          return (
                            <div key={gi} className={`rounded-lg border ${color.border} ${color.bg} p-2`}>
                              <span className={`text-[10px] font-semibold uppercase ${color.text}`}>{getGroupLabel(gi)}</span>
                              <div className="flex flex-wrap gap-1.5 mt-1">
                                {urls.map((url, idx) => {
                                  const fullUrl = url.startsWith('/api/') ? `${import.meta.env.VITE_API_URL || ''}${url}` : url;
                                  const mediaType = getMediaTypeFromUrl(url);
                                  return (
                                    <div key={idx} className="w-16 h-16 rounded border border-gray-200 overflow-hidden bg-white">
                                      {mediaType === 'video' ? (
                                        renderVideoThumb(url, fullUrl)
                                      ) : (
                                        <img
                                          src={fullUrl}
                                          alt={`${getGroupLabel(gi)} media ${idx + 1}`}
                                          className="w-full h-full object-cover"
                                          onError={(e) => {
                                            (e.target as HTMLImageElement).style.display = 'none';
                                          }}
                                        />
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex items-center space-x-3 ml-4">
                {/* Randomization pool: none, existing pools, or new pool */}
                {!readOnly && (
                  <div className="group relative flex flex-col items-end gap-0.5">
                    <label className="sr-only" htmlFor={`randomization-pool-${question.id}`}>
                      Randomization pool
                    </label>
                    <select
                      id={`randomization-pool-${question.id}`}
                      value={question.randomization_group ?? ''}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        e.stopPropagation();
                        const v = e.target.value;
                        if (v === '__new__') {
                          const ids = questions
                            .map((q) => q.randomization_group)
                            .filter((g): g is string => Boolean(g));
                          updateQuestion(question.id, {
                            randomization_group: nextRandomizationPoolId(ids),
                          });
                        } else {
                          updateQuestion(question.id, {
                            randomization_group: v === '' ? null : v,
                          });
                        }
                      }}
                      className="max-w-[9.5rem] text-xs border border-gray-300 rounded-md px-1.5 py-1 bg-white text-gray-800 shadow-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      title="Randomization pool (questions shuffle only within the same pool)"
                    >
                      <option value="">None</option>
                      <option value="default">{formatRandomizationPoolLabel('default')}</option>
                      {randomizationPoolsInSurvey
                        .filter((pid) => pid !== 'default')
                        .map((pid) => (
                          <option key={pid} value={pid}>
                            {formatRandomizationPoolLabel(pid)}
                          </option>
                        ))}
                      <option value="__new__">New pool…</option>
                    </select>
                    <span className="absolute bottom-full right-0 mb-1 px-2 py-1 text-xs font-medium text-white bg-gray-900 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 max-w-[14rem] text-right">
                      Shuffle with others in the same pool only
                    </span>
                  </div>
                )}
                {/* Preview from here button */}
                {(surveyId || groupSurveyIds.length > 0) && (
                  <div className="relative">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePreviewClick(index);
                      }}
                      className="group relative text-gray-400 hover:text-blue-600 transition-colors"
                      title="Preview from this question"
                    >
                      <Eye className="w-4 h-4" />
                      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs font-medium text-white bg-gray-900 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                        Preview from here
                      </span>
                    </button>
                    {previewPickerFor === index && (
                      <>
                      <div className="fixed inset-0 z-20" onClick={(e) => { e.stopPropagation(); setPreviewPickerFor(null); }} />
                      <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-30 min-w-[160px]">
                        <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 border-b border-gray-100">Select group to preview</div>
                        {groupSurveyIds.map((sid, idx) => (
                          <button
                            key={sid}
                            type="button"
                            onClick={(e) => { e.stopPropagation(); openPreview(sid, index); }}
                            className="flex items-center w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-700"
                          >
                            <span className={`inline-block w-2 h-2 rounded-full mr-2 ${getGroupColor(idx).header}`} />
                            {groupLabels[idx] || `Group ${idx + 1}`}
                          </button>
                        ))}
                      </div>
                      </>
                    )}
                  </div>
                )}
                {/* Drag Handle - positioned on the right */}
                {!readOnly && !editingQuestionId && (
                  <button
                    type="button"
                    className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label="Drag to reorder"
                  >
                    <GripVertical className="w-5 h-5" />
                  </button>
                )}
                {editingQuestionId === question.id ? (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        // Build redirect config
                        let redirectConfig: RedirectConfig | null = null;
                        if (redirectBaseUrl.trim()) {
                          const completionParamsObj = parseParams(completionParams);
                          const screenOutParamsObj = parseParams(screenOutParams);
                          const quotaFullParamsObj = parseParams(quotaFullParams);

                          const trackingParam: { [key: string]: string } = {};
                          if (trackingParamName.trim()) {
                            trackingParam[trackingParamName.trim()] = '{TRACKING_ID}';
                          }

                          redirectConfig = {
                            redirect_base_url: redirectBaseUrl.trim(),
                            screened_out_url: screenedOutUrl.trim() || null,
                            tracking_param: trackingParam,
                            completion_params: Object.keys(completionParamsObj).length > 0 ? completionParamsObj : null,
                            screen_out_params: Object.keys(screenOutParamsObj).length > 0 ? screenOutParamsObj : null,
                            quota_full_params: Object.keys(quotaFullParamsObj).length > 0 ? quotaFullParamsObj : null,
                          };
                        }

                        // Parse maxResponses and minLinkedInConnections
                        const maxResponsesValue = maxResponses.trim() ? parseInt(maxResponses) : null;
                        const minLinkedInConnectionsValue = minLinkedInConnections.trim() ? parseInt(minLinkedInConnections) : null;

                        // Save changes to parent with quota, LinkedIn, and redirect config
                        onQuestionsUpdated(questions, redirectConfig, maxResponsesValue, blocks, minLinkedInConnectionsValue);
                        setEditingQuestionId(null);
                        setOriginalQuestionBeforeEdit(null);
                        setHasChanges(false);
                      }}
                      icon={<Save className="w-4 h-4" />}
                    >
                      {UI_TEXT.AI_SURVEY.QUESTIONS_PREVIEW.BUTTONS.SAVE}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        // Restore original question if we have it
                        if (originalQuestionBeforeEdit) {
                          const restoredQuestions = questions.map(q =>
                            q.id === originalQuestionBeforeEdit.id ? originalQuestionBeforeEdit : q
                          );
                          setQuestions(restoredQuestions);
                        }
                        setEditingQuestionId(null);
                        setOriginalQuestionBeforeEdit(null);
                        setHasChanges(false);
                      }}
                      icon={<X className="w-4 h-4" />}
                    >
                      {UI_TEXT.AI_SURVEY.QUESTIONS_PREVIEW.BUTTONS.CANCEL}
                    </Button>
                  </>
                ) : (
                  !readOnly && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setOriginalQuestionBeforeEdit(question);
                          setEditingQuestionId(question.id);
                        }}
                        icon={<Edit3 className="w-4 h-4" />}
                      >
                        {UI_TEXT.AI_SURVEY.QUESTIONS_PREVIEW.BUTTONS.EDIT}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => duplicateQuestion(question.id)}
                        icon={<Copy className="w-4 h-4" />}
                      >
                        Duplicate
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeQuestion(question.id)}
                        icon={<Trash2 className="w-4 h-4 text-red-600" />}
                        className="text-red-600 hover:text-red-700"
                      >
                        Delete
                      </Button>
                    </>
                  )
                )}
              </div>
            </div>

            {/* Question Details */}
            <div className="mt-3 space-y-3">
              {/* Choices for choice-based questions */}
              {hasChoices(question.type) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {UI_TEXT.AI_SURVEY.QUESTIONS_PREVIEW.LABELS.ANSWER_CHOICES}
                  </label>
                  <div className="space-y-3">
                    {(question.choices || []).map((choice, choiceIndex) => {
                      const supportsMedia = MEDIA_SUPPORTED_TYPES.includes(question.type);
                      const hasMediaContent = (question.media_urls && question.media_urls.length > 0)
                        || (question.option_media && Object.keys(question.option_media).length > 0)
                        || (question.group_media && Object.keys(question.group_media).length > 0)
                        || (question.option_group_media && Object.keys(question.option_group_media).length > 0);
                      const showMediaLayout = supportsMedia && (hasMediaContent || editingQuestionId === question.id);

                      return (
                        <div key={choiceIndex} className={`${showMediaLayout ? 'border border-gray-200 rounded-lg p-3' : 'flex items-center space-x-2'}`}>
                          {showMediaLayout ? (
                            // Media + Choice Layout
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2 flex-1">
                                  <span className="text-sm text-gray-500 w-8">
                                    {choiceIndex + 1}.
                                  </span>
                                  {editingQuestionId === question.id ? (
                                    <>
                                      <input
                                        type="text"
                                        value={choice}
                                        onChange={(e) => updateChoice(question.id, choiceIndex, e.target.value)}
                                        className="flex-1 px-3 py-1 border border-gray-300 rounded text-sm"
                                      />
                                      {/* Option-level Media Upload Icon */}
                                      <MediaUploadIcon
                                        hasMedia={getOptionMediaUrls(question, choiceIndex).length > 0}
                                        mediaCount={getOptionMediaUrls(question, choiceIndex).length}
                                        onClick={() => setExpandedOptionMedia(
                                          expandedOptionMedia?.questionId === question.id && expandedOptionMedia?.optionIndex === choiceIndex
                                            ? null
                                            : { questionId: question.id, optionIndex: choiceIndex }
                                        )}
                                      />
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => removeChoice(question.id, choiceIndex)}
                                        icon={<X className="w-3 h-3" />}
                                      >
                                      </Button>
                                    </>
                                  ) : (
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium text-gray-900">{choice}</span>
                                      </div>
                                      {/* Option media preview in read mode */}
                                      {getOptionMediaUrls(question, choiceIndex).length > 0 && (
                                        <div className="mt-2 flex flex-wrap gap-2">
                                          {getOptionMediaUrls(question, choiceIndex).map((url, mediaIdx) => {
                                            const fullUrl = url.startsWith('/api/')
                                              ? `${import.meta.env.VITE_API_URL || ''}${url}`
                                              : url;
                                            const mediaType = getMediaTypeFromUrl(url);
                                            return (
                                              <div key={mediaIdx} className="w-14 h-14 rounded-md border border-gray-200 overflow-hidden bg-gray-50">
                                                {mediaType === 'video' ? (
                                                  renderVideoThumb(url, fullUrl)
                                                ) : (
                                                  <img
                                                    src={fullUrl}
                                                    alt={`${choice} media ${mediaIdx + 1}`}
                                                    className="w-full h-full object-cover"
                                                    onError={(e) => {
                                                      const target = e.target as HTMLImageElement;
                                                      target.style.display = 'none';
                                                    }}
                                                  />
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                      {/* Option group media preview in read mode */}
                                      {groupCount > 1 && question.option_group_media?.[String(choiceIndex)] && Object.keys(question.option_group_media[String(choiceIndex)]).length > 0 && (
                                        <div className="mt-2 space-y-1.5">
                                          {(question.visible_groups ?? Array.from({ length: groupCount }, (_, i) => i)).map((gi) => {
                                            const urls = question.option_group_media?.[String(choiceIndex)]?.[String(gi)];
                                            if (!urls || urls.length === 0) return null;
                                            const color = getGroupColor(gi);
                                            return (
                                              <div key={gi} className={`rounded border ${color.border} ${color.bg} px-2 py-1 flex items-center gap-2`}>
                                                <span className={`text-[9px] font-semibold uppercase ${color.text} shrink-0`}>{getGroupLabel(gi)}</span>
                                                <div className="flex flex-wrap gap-1">
                                                  {urls.map((url, idx) => {
                                                    const fullUrl = url.startsWith('/api/') ? `${import.meta.env.VITE_API_URL || ''}${url}` : url;
                                                    const mediaType = getMediaTypeFromUrl(url);
                                                    return (
                                                      <div key={idx} className="w-10 h-10 rounded border border-gray-200 overflow-hidden bg-white">
                                                        {mediaType === 'video' ? (
                                                          renderVideoThumb(url, fullUrl)
                                                        ) : (
                                                          <img
                                                            src={fullUrl}
                                                            alt={`${getGroupLabel(gi)} ${choice} ${idx + 1}`}
                                                            className="w-full h-full object-cover"
                                                            onError={(e) => {
                                                              (e.target as HTMLImageElement).style.display = 'none';
                                                            }}
                                                          />
                                                        )}
                                                      </div>
                                                    );
                                                  })}
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Option-level Media Grid - show if has images OR is explicitly expanded */}
                              {editingQuestionId === question.id && 
                               (getOptionMediaUrls(question, choiceIndex).length > 0 ||
                                (expandedOptionMedia?.questionId === question.id && 
                                 expandedOptionMedia?.optionIndex === choiceIndex)) && (
                                <div className="ml-10 p-2 bg-gray-50 rounded-lg border border-gray-200">
                                  <div className="text-xs font-medium text-gray-600 mb-2">Option Media</div>
                                  {groupCount > 1 ? (
                                    <GroupMediaUpload
                                      groupCount={groupCount}
                                      visibleGroups={question.visible_groups}
                                      groupMedia={question.option_group_media?.[String(choiceIndex)] || {}}
                                      onMediaAdd={(gi, url) => {
                                        setQuestions(prev => prev.map(q => {
                                          if (q.id !== question.id) return q;
                                          const ogm = { ...(q.option_group_media || {}) };
                                          const perOpt = { ...(ogm[String(choiceIndex)] || {}) };
                                          perOpt[String(gi)] = [...(perOpt[String(gi)] || []), url];
                                          ogm[String(choiceIndex)] = perOpt;
                                          return { ...q, option_group_media: ogm };
                                        }));
                                        setHasChanges(true);
                                      }}
                                      onMediaRemove={(gi, removeIdx) => {
                                        setQuestions(prev => prev.map(q => {
                                          if (q.id !== question.id) return q;
                                          const ogm = { ...(q.option_group_media || {}) };
                                          const perOpt = { ...(ogm[String(choiceIndex)] || {}) };
                                          const arr = (perOpt[String(gi)] || []).filter((_, i) => i !== removeIdx);
                                          if (arr.length > 0) perOpt[String(gi)] = arr;
                                          else delete perOpt[String(gi)];
                                          if (Object.keys(perOpt).length > 0) ogm[String(choiceIndex)] = perOpt;
                                          else delete ogm[String(choiceIndex)];
                                          return { ...q, option_group_media: Object.keys(ogm).length > 0 ? ogm : undefined };
                                        }));
                                        setHasChanges(true);
                                      }}
                                      onMediaReplace={(gi, mediaIdx, newUrl) => {
                                        setQuestions(prev => prev.map(q => {
                                          if (q.id !== question.id) return q;
                                          const ogm = { ...(q.option_group_media || {}) };
                                          const perOpt = { ...(ogm[String(choiceIndex)] || {}) };
                                          const arr = [...(perOpt[String(gi)] || [])];
                                          if (mediaIdx < 0 || mediaIdx >= arr.length) return q;
                                          arr[mediaIdx] = newUrl;
                                          perOpt[String(gi)] = arr;
                                          ogm[String(choiceIndex)] = perOpt;
                                          return { ...q, option_group_media: ogm };
                                        }));
                                        setHasChanges(true);
                                      }}
                                      maxItemsPerGroup={3}
                                      compact
                                    />
                                  ) : (
                                    <MediaUploadGrid
                                      mediaUrls={getOptionMediaUrls(question, choiceIndex)}
                                      onMediaAdd={(url) => addOptionMedia(question.id, choiceIndex, url)}
                                      onMediaRemove={(index) => removeOptionMedia(question.id, choiceIndex, index)}
                                      onMediaReplace={(index, url) =>
                                        replaceOptionMedia(question.id, choiceIndex, index, url)
                                      }
                                      onMediaReorder={(from, to) =>
                                        reorderOptionMedia(question.id, choiceIndex, from, to)
                                      }
                                      maxItems={3}
                                      compact
                                    />
                                  )}
                                </div>
                              )}

                              {/* Action Display in Read Mode - only show if action exists and not editing */}
                              {(readOnly || editingQuestionId !== question.id) && CONDITIONAL_ACTION_TYPES.includes(question.type) && getChoiceAction(question, choiceIndex)?.action && (
                                <div className="ml-10 mt-1 flex items-center space-x-2">
                                  {getChoiceAction(question, choiceIndex)?.action === 'terminate' && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                      🚫 Terminates survey
                                      {getChoiceAction(question, choiceIndex)?.reason && ` (${getChoiceAction(question, choiceIndex)?.reason})`}
                                    </span>
                                  )}
                                  {getChoiceAction(question, choiceIndex)?.action === 'skip_to' && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                      ➜ Skips to: {questions.find(q => q.id === getChoiceAction(question, choiceIndex)?.target)?.text.substring(0, 40) || 'Unknown question'}...
                                    </span>
                                  )}
                                </div>
                              )}

                              {/* Action Configuration for Media Layout - only show in edit mode for conditional action types */}
                              {editingQuestionId === question.id && CONDITIONAL_ACTION_TYPES.includes(question.type) && (
                                <div className="ml-10 mt-2 flex items-center space-x-2">
                                  <label className="text-xs text-gray-600 min-w-fit">Action:</label>
                                  <select
                                    value={getChoiceAction(question, choiceIndex)?.action || 'none'}
                                    onChange={(e) => {
                                      const action = e.target.value === 'none' ? undefined : e.target.value as 'terminate' | 'skip_to';
                                      updateChoiceAction(question.id, choiceIndex, action);
                                    }}
                                    className="px-2 py-1 border border-gray-300 rounded text-xs"
                                  >
                                    <option value="none">Continue normally</option>
                                    <option value="terminate">Terminate (disqualify)</option>
                                    <option value="skip_to">Skip to question</option>
                                  </select>

                                  {getChoiceAction(question, choiceIndex)?.action === 'skip_to' && (
                                    <select
                                      value={getChoiceAction(question, choiceIndex)?.target || ''}
                                      onChange={(e) => updateChoiceAction(question.id, choiceIndex, 'skip_to', e.target.value)}
                                      className="px-2 py-1 border border-gray-300 rounded text-xs"
                                    >
                                      <option value="">Select question...</option>
                                      {questions.map(q => (
                                        <option key={q.id} value={q.id}>{q.text.substring(0, 50)}{q.text.length > 50 ? '...' : ''}</option>
                                      ))}
                                    </select>
                                  )}

                                  {getChoiceAction(question, choiceIndex)?.action === 'terminate' && (
                                    <input
                                      type="text"
                                      value={getChoiceAction(question, choiceIndex)?.reason || ''}
                                      onChange={(e) => updateChoiceAction(question.id, choiceIndex, 'terminate', undefined, e.target.value)}
                                      placeholder="Reason (optional)"
                                      className="px-2 py-1 border border-gray-300 rounded text-xs flex-1"
                                    />
                                  )}
                                </div>
                              )}
                            </div>
                          ) : (
                            // Regular Choice Layout
                            <div className="flex flex-col space-y-2 w-full">
                              <div className="flex items-center space-x-2">
                                <span className="text-sm text-gray-500 w-8">
                                  {choiceIndex + 1}.
                                </span>
                                {editingQuestionId === question.id ? (
                                  <>
                                    <input
                                      type="text"
                                      value={choice}
                                      onChange={(e) => updateChoice(question.id, choiceIndex, e.target.value)}
                                      className="flex-1 px-3 py-1 border border-gray-300 rounded text-sm"
                                    />
                                    {/* Option-level Media Upload Icon for regular layout */}
                                    {supportsMedia && (
                                      <MediaUploadIcon
                                        hasMedia={getOptionMediaUrls(question, choiceIndex).length > 0}
                                        mediaCount={getOptionMediaUrls(question, choiceIndex).length}
                                        onClick={() => setExpandedOptionMedia(
                                          expandedOptionMedia?.questionId === question.id && expandedOptionMedia?.optionIndex === choiceIndex
                                            ? null
                                            : { questionId: question.id, optionIndex: choiceIndex }
                                        )}
                                      />
                                    )}
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => removeChoice(question.id, choiceIndex)}
                                      icon={<X className="w-3 h-3" />}
                                    >
                                    </Button>
                                  </>
                                ) : (
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm text-gray-900">{choice}</span>
                                    </div>
                                    {/* Option media preview in read mode */}
                                    {getOptionMediaUrls(question, choiceIndex).length > 0 && (
                                      <div className="mt-2 flex flex-wrap gap-2">
                                        {getOptionMediaUrls(question, choiceIndex).map((url, mediaIdx) => {
                                          const fullUrl = url.startsWith('/api/')
                                            ? `${import.meta.env.VITE_API_URL || ''}${url}`
                                            : url;
                                          const mediaType = getMediaTypeFromUrl(url);
                                          return (
                                            <div key={mediaIdx} className="w-14 h-14 rounded-md border border-gray-200 overflow-hidden bg-gray-50">
                                              {mediaType === 'video' ? (
                                                renderVideoThumb(url, fullUrl)
                                              ) : (
                                                <img
                                                  src={fullUrl}
                                                  alt={`${choice} media ${mediaIdx + 1}`}
                                                  className="w-full h-full object-cover"
                                                  onError={(e) => {
                                                    const target = e.target as HTMLImageElement;
                                                    target.style.display = 'none';
                                                  }}
                                                />
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>

                              {/* Option-level Media Grid for regular layout - show if has images OR is explicitly expanded */}
                              {editingQuestionId === question.id && 
                               supportsMedia &&
                               (getOptionMediaUrls(question, choiceIndex).length > 0 ||
                                (expandedOptionMedia?.questionId === question.id && 
                                 expandedOptionMedia?.optionIndex === choiceIndex)) && (
                                <div className="ml-10 p-2 bg-gray-50 rounded-lg border border-gray-200">
                                  <div className="text-xs font-medium text-gray-600 mb-2">Option Media</div>
                                  {groupCount > 1 ? (
                                    <GroupMediaUpload
                                      groupCount={groupCount}
                                      visibleGroups={question.visible_groups}
                                      groupMedia={question.option_group_media?.[String(choiceIndex)] || {}}
                                      onMediaAdd={(gi, url) => {
                                        setQuestions(prev => prev.map(q => {
                                          if (q.id !== question.id) return q;
                                          const ogm = { ...(q.option_group_media || {}) };
                                          const perOpt = { ...(ogm[String(choiceIndex)] || {}) };
                                          perOpt[String(gi)] = [...(perOpt[String(gi)] || []), url];
                                          ogm[String(choiceIndex)] = perOpt;
                                          return { ...q, option_group_media: ogm };
                                        }));
                                        setHasChanges(true);
                                      }}
                                      onMediaRemove={(gi, removeIdx) => {
                                        setQuestions(prev => prev.map(q => {
                                          if (q.id !== question.id) return q;
                                          const ogm = { ...(q.option_group_media || {}) };
                                          const perOpt = { ...(ogm[String(choiceIndex)] || {}) };
                                          const arr = (perOpt[String(gi)] || []).filter((_, i) => i !== removeIdx);
                                          if (arr.length > 0) perOpt[String(gi)] = arr;
                                          else delete perOpt[String(gi)];
                                          if (Object.keys(perOpt).length > 0) ogm[String(choiceIndex)] = perOpt;
                                          else delete ogm[String(choiceIndex)];
                                          return { ...q, option_group_media: Object.keys(ogm).length > 0 ? ogm : undefined };
                                        }));
                                        setHasChanges(true);
                                      }}
                                      onMediaReplace={(gi, mediaIdx, newUrl) => {
                                        setQuestions(prev => prev.map(q => {
                                          if (q.id !== question.id) return q;
                                          const ogm = { ...(q.option_group_media || {}) };
                                          const perOpt = { ...(ogm[String(choiceIndex)] || {}) };
                                          const arr = [...(perOpt[String(gi)] || [])];
                                          if (mediaIdx < 0 || mediaIdx >= arr.length) return q;
                                          arr[mediaIdx] = newUrl;
                                          perOpt[String(gi)] = arr;
                                          ogm[String(choiceIndex)] = perOpt;
                                          return { ...q, option_group_media: ogm };
                                        }));
                                        setHasChanges(true);
                                      }}
                                      maxItemsPerGroup={3}
                                      compact
                                    />
                                  ) : (
                                    <MediaUploadGrid
                                      mediaUrls={getOptionMediaUrls(question, choiceIndex)}
                                      onMediaAdd={(url) => addOptionMedia(question.id, choiceIndex, url)}
                                      onMediaRemove={(index) => removeOptionMedia(question.id, choiceIndex, index)}
                                      onMediaReplace={(index, url) =>
                                        replaceOptionMedia(question.id, choiceIndex, index, url)
                                      }
                                      onMediaReorder={(from, to) =>
                                        reorderOptionMedia(question.id, choiceIndex, from, to)
                                      }
                                      maxItems={3}
                                      compact
                                    />
                                  )}
                                </div>
                              )}

                              {/* Action Display in Read Mode - only show if action exists and not editing */}
                              {(readOnly || editingQuestionId !== question.id) && CONDITIONAL_ACTION_TYPES.includes(question.type) && getChoiceAction(question, choiceIndex)?.action && (
                                <div className="ml-10 mt-1 flex items-center space-x-2">
                                  {getChoiceAction(question, choiceIndex)?.action === 'terminate' && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                      🚫 Terminates survey
                                      {getChoiceAction(question, choiceIndex)?.reason && ` (${getChoiceAction(question, choiceIndex)?.reason})`}
                                    </span>
                                  )}
                                  {getChoiceAction(question, choiceIndex)?.action === 'skip_to' && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                      ➜ Skips to: {questions.find(q => q.id === getChoiceAction(question, choiceIndex)?.target)?.text.substring(0, 40) || 'Unknown question'}...
                                    </span>
                                  )}
                                </div>
                              )}

                              {/* Action Configuration for Regular Layout - only show in edit mode for conditional action types */}
                              {editingQuestionId === question.id && CONDITIONAL_ACTION_TYPES.includes(question.type) && (
                                <div className="ml-10 flex items-center space-x-2">
                                  <label className="text-xs text-gray-600 min-w-fit">Action:</label>
                                  <select
                                    value={getChoiceAction(question, choiceIndex)?.action || 'none'}
                                    onChange={(e) => {
                                      const action = e.target.value === 'none' ? undefined : e.target.value as 'terminate' | 'skip_to';
                                      updateChoiceAction(question.id, choiceIndex, action);
                                    }}
                                    className="px-2 py-1 border border-gray-300 rounded text-xs"
                                  >
                                    <option value="none">Continue normally</option>
                                    <option value="terminate">Terminate (disqualify)</option>
                                    <option value="skip_to">Skip to question</option>
                                  </select>

                                  {getChoiceAction(question, choiceIndex)?.action === 'skip_to' && (
                                    <select
                                      value={getChoiceAction(question, choiceIndex)?.target || ''}
                                      onChange={(e) => updateChoiceAction(question.id, choiceIndex, 'skip_to', e.target.value)}
                                      className="px-2 py-1 border border-gray-300 rounded text-xs"
                                    >
                                      <option value="">Select question...</option>
                                      {questions.map(q => (
                                        <option key={q.id} value={q.id}>{q.text.substring(0, 50)}{q.text.length > 50 ? '...' : ''}</option>
                                      ))}
                                    </select>
                                  )}

                                  {getChoiceAction(question, choiceIndex)?.action === 'terminate' && (
                                    <input
                                      type="text"
                                      value={getChoiceAction(question, choiceIndex)?.reason || ''}
                                      onChange={(e) => updateChoiceAction(question.id, choiceIndex, 'terminate', undefined, e.target.value)}
                                      placeholder="Reason (optional)"
                                      className="px-2 py-1 border border-gray-300 rounded text-xs flex-1"
                                    />
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    
                    {/* Show "Other" indicator when allow_other is enabled - only for single_choice and multiple_choice WITHOUT media */}
                    {question.allow_other && editingQuestionId !== question.id &&
                     (question.type === 'single_choice' || question.type === 'multiple_choice') &&
                     (!question.media_urls || question.media_urls.length === 0) && (
                      <div className="flex items-center space-x-2 mt-2">
                        <span className="text-sm text-gray-500 w-8">
                          +
                        </span>
                        <span className="text-sm text-gray-600">
                          {UI_TEXT.AI_SURVEY.QUESTIONS_PREVIEW.LABELS.OTHER_OPTION_INDICATOR}
                        </span>
                      </div>
                    )}

                    {/* Show "Randomized" indicator when randomize_choices is enabled - for single_choice and multiple_choice */}
                    {question.randomize_choices && editingQuestionId !== question.id &&
                     (question.type === 'single_choice' || question.type === 'multiple_choice') && (
                      <div className="flex items-center space-x-2 mt-2">
                        <span className="text-sm text-gray-500 w-8">
                          ~
                        </span>
                        <span className="text-sm text-gray-600">
                          Choices will be randomized
                        </span>
                      </div>
                    )}
                    {(question.choices || []).length === 0 && editingQuestionId !== question.id && (
                      <span className="text-sm text-gray-500 italic">No options yet</span>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (editingQuestionId !== question.id) {
                          setOriginalQuestionBeforeEdit(question);
                          setEditingQuestionId(question.id);
                        }
                        addChoice(question.id);
                      }}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      {UI_TEXT.AI_SURVEY.QUESTIONS_PREVIEW.BUTTONS.ADD_CHOICE}
                    </Button>
                    {editingQuestionId === question.id && (
                      <div className="space-y-2">
                        {/* Allow Other Option Toggle - only for single_choice and multiple_choice WITHOUT media */}
                        {(question.type === 'single_choice' || question.type === 'multiple_choice') &&
                         (!question.media_urls || question.media_urls.length === 0) && (
                          <div className="flex items-center space-x-2 pt-2">
                            <input
                              type="checkbox"
                              id={`allow-other-${question.id}`}
                              checked={question.allow_other || false}
                              onChange={(e) => updateQuestion(question.id, { allow_other: e.target.checked })}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <label
                              htmlFor={`allow-other-${question.id}`}
                              className="text-sm text-gray-700 cursor-pointer"
                            >
                              {UI_TEXT.AI_SURVEY.QUESTIONS_PREVIEW.LABELS.ALLOW_OTHER_OPTION}
                            </label>
                          </div>
                        )}

                        {/* Randomize Responses Toggle - only for single_choice and multiple_choice */}
                        {(question.type === 'single_choice' || question.type === 'multiple_choice') && (
                          <div className="flex items-center space-x-2 pt-2">
                            <input
                              type="checkbox"
                              id={`randomize-choices-${question.id}`}
                              checked={question.randomize_choices || false}
                              onChange={(e) => updateQuestion(question.id, { randomize_choices: e.target.checked })}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <label
                              htmlFor={`randomize-choices-${question.id}`}
                              className="text-sm text-gray-700 cursor-pointer"
                            >
                              Randomize Responses
                            </label>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Scale settings for scale-based questions */}
              {hasScale(question.type) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Scale Settings
                  </label>
                  {editingQuestionId === question.id ? (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">
                          Min Value
                        </label>
                        <input
                          type="number"
                          value={question.scale_min || (question.type === 'nps' ? 0 : 1)}
                          onChange={(e) => updateQuestion(question.id, { scale_min: parseInt(e.target.value) })}
                          className="w-full px-3 py-1 border border-gray-300 rounded text-sm"
                          min={question.type === 'nps' ? 0 : 1}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">
                          Max Value
                        </label>
                        <input
                          type="number"
                          value={question.scale_max || (question.type === 'nps' ? 10 : 5)}
                          onChange={(e) => updateQuestion(question.id, { scale_max: parseInt(e.target.value) })}
                          className="w-full px-3 py-1 border border-gray-300 rounded text-sm"
                          min={question.type === 'nps' ? 0 : 1}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-4 text-sm text-gray-700">
                      <span>
                        <span className="font-medium">Min:</span> {question.scale_min || (question.type === 'nps' ? 0 : 1)}
                      </span>
                      <span>
                        <span className="font-medium">Max:</span> {question.scale_max || (question.type === 'nps' ? 10 : 5)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Ranking settings for ranking questions */}
              {hasRanking(question.type) && editingQuestionId === question.id && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ranking Settings
                  </label>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      Number of items to rank
                    </label>
                    <input
                      type="number"
                      value={question.ranking_count || 3}
                      onChange={(e) => updateQuestion(question.id, { ranking_count: parseInt(e.target.value) })}
                      className="w-full px-3 py-1 border border-gray-300 rounded text-sm"
                      min={1}
                      max={question.choices?.length || 5}
                    />
                  </div>
                </div>
              )}

              {/* Matrix settings for matrix_rating questions */}
              {hasMatrix(question.type) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Matrix/Grid Settings
                  </label>

                  {/* Matrix Items */}
                  <div className="mb-4">
                    <label className="block text-xs text-gray-600 mb-1">
                      Items to Rate
                    </label>
                    <div className="space-y-2">
                      {(question.matrix_rows || []).map((item, itemIndex) => (
                        <div key={itemIndex} className="flex items-center space-x-2">
                          <span className="text-sm text-gray-500 w-8">
                            {itemIndex + 1}.
                          </span>
                          {editingQuestionId === question.id ? (
                            <>
                              <input
                                type="text"
                                value={item}
                                onChange={(e) => updateMatrixItem(question.id, itemIndex, e.target.value)}
                                className="flex-1 px-3 py-1 border border-gray-300 rounded text-sm"
                                placeholder="Item to rate"
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeMatrixItem(question.id, itemIndex)}
                                icon={<X className="w-3 h-3" />}
                              >
                              </Button>
                            </>
                          ) : (
                            <span className="text-sm text-gray-900">{item}</span>
                          )}
                        </div>
                      ))}

                      {(!question.matrix_rows || question.matrix_rows.length === 0) && editingQuestionId !== question.id && (
                        <span className="text-sm text-gray-500 italic">No items defined</span>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (editingQuestionId !== question.id) {
                            setOriginalQuestionBeforeEdit(question);
                            setEditingQuestionId(question.id);
                          }
                          addMatrixItem(question.id);
                        }}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        Add Matrix Item
                      </Button>
                    </div>
                  </div>

                  {/* Scale Labels */}
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      Scale Labels
                    </label>
                    {editingQuestionId === question.id ? (
                      <input
                        type="text"
                        value={question.matrix_columns?.join(', ') || '1, 2, 3, 4, 5'}
                        onChange={(e) => updateQuestion(question.id, {
                          matrix_columns: e.target.value.split(',').map(s => s.trim()).filter(s => s)
                        })}
                        className="w-full px-3 py-1 border border-gray-300 rounded text-sm"
                        placeholder="1, 2, 3, 4, 5"
                      />
                    ) : (
                      <div className="text-sm text-gray-900">
                        {question.matrix_columns?.join(', ') || '1, 2, 3, 4, 5'}
                      </div>
                    )}
                  </div>

                  {/* Randomize Items Toggle - only in edit mode */}
                  {editingQuestionId === question.id && (
                    <div className="flex items-center space-x-2 pt-2">
                      <input
                        type="checkbox"
                        id={`randomize-matrix-${question.id}`}
                        checked={question.randomize_choices || false}
                        onChange={(e) => updateQuestion(question.id, { randomize_choices: e.target.checked })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label
                        htmlFor={`randomize-matrix-${question.id}`}
                        className="text-sm text-gray-700 cursor-pointer"
                      >
                        Randomize Items (scale labels remain fixed)
                      </label>
                    </div>
                  )}

                  {/* Show "Randomized" indicator when randomize_choices is enabled */}
                  {question.randomize_choices && editingQuestionId !== question.id && (
                    <div className="flex items-center space-x-2 mt-2 pt-2 border-t border-gray-200">
                      <span className="text-sm text-gray-500">
                        ~
                      </span>
                      <span className="text-sm text-gray-600">
                        Items will be randomized (scale labels remain fixed)
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Percentage Allocation settings */}
              {hasAllocation(question.type) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Percentage Allocation Settings
                  </label>

                  {editingQuestionId === question.id && (
                    <p className="text-xs text-gray-500 mb-2">
                      Respondents will allocate percentages across the options. The total must equal the target (default: 100%).
                    </p>
                  )}

                  {/* Allocation Options */}
                  <div className="mb-4">
                    <label className="block text-xs text-gray-600 mb-1">
                      Options to Allocate
                    </label>
                    <div className="space-y-2">
                      {(question.allocation_options || question.choices || []).map((option: string, optionIndex: number) => (
                        <div key={optionIndex} className="flex items-center space-x-2">
                          <span className="text-sm text-gray-500 w-8">
                            {optionIndex + 1}.
                          </span>
                          {editingQuestionId === question.id ? (
                            <>
                              <input
                                type="text"
                                value={option}
                                onChange={(e) => {
                                  const options = [...(question.allocation_options || question.choices || [])];
                                  options[optionIndex] = e.target.value;
                                  updateQuestion(question.id, { allocation_options: options, choices: options });
                                }}
                                className="flex-1 px-3 py-1 border border-gray-300 rounded text-sm"
                                placeholder="Option name"
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const options = (question.allocation_options || question.choices || []).filter((_: string, i: number) => i !== optionIndex);
                                  updateQuestion(question.id, { allocation_options: options, choices: options });
                                }}
                                icon={<X className="w-3 h-3" />}
                              >
                              </Button>
                            </>
                          ) : (
                            <span className="text-sm text-gray-900">{option}</span>
                          )}
                        </div>
                      ))}

                      {editingQuestionId === question.id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const options = [...(question.allocation_options || question.choices || []), `Option ${(question.allocation_options || question.choices || []).length + 1}`];
                            updateQuestion(question.id, { allocation_options: options, choices: options });
                          }}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          Add Option
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Allocation Target */}
                  {editingQuestionId === question.id && (
                    <div className="mb-4">
                      <label className="block text-xs text-gray-600 mb-1">
                        Target Total
                      </label>
                      <input
                        type="number"
                        value={question.allocation_target || 100}
                        onChange={(e) => updateQuestion(question.id, { allocation_target: parseInt(e.target.value) || 100 })}
                        className="w-24 px-3 py-1 border border-gray-300 rounded text-sm"
                        min={1}
                        max={1000}
                      />
                    </div>
                  )}

                  {/* Auto-fill Last & Allow Zero toggles */}
                  {editingQuestionId === question.id && (
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`auto-fill-${question.id}`}
                          checked={question.auto_fill_last !== false}
                          onChange={(e) => updateQuestion(question.id, { auto_fill_last: e.target.checked })}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label
                          htmlFor={`auto-fill-${question.id}`}
                          className="text-sm text-gray-700 cursor-pointer"
                        >
                          Auto-calculate last option
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`allow-zero-${question.id}`}
                          checked={question.allow_zero !== false}
                          onChange={(e) => updateQuestion(question.id, { allow_zero: e.target.checked })}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label
                          htmlFor={`allow-zero-${question.id}`}
                          className="text-sm text-gray-700 cursor-pointer"
                        >
                          Allow 0 for individual options
                        </label>
                      </div>
                    </div>
                  )}

                  {/* Show settings summary when not editing */}
                  {editingQuestionId !== question.id && (
                    <div className="text-sm text-gray-600 mt-2">
                      Target: {question.allocation_target || 100}
                      {question.auto_fill_last !== false && ' • Auto-fill last option'}
                      {question.allow_zero === false && ' • No zeros allowed'}
                    </div>
                  )}
                </div>
              )}

              {/* MaxDiff settings for max_diff questions */}
              {hasMaxDiff(question.type) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    MaxDiff Settings
                  </label>

                  {editingQuestionId === question.id && (
                    <p className="text-xs text-gray-500 mb-2">
                      MaxDiff questions present respondents with sets of options where they select the most and least appealing choices. Edit the claims/options below.
                    </p>
                  )}

                  {/* MaxDiff Claims/Options */}
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      Claims/Options
                    </label>
                    <div className="space-y-2">
                      {(question.choices || []).map((choice, choiceIndex) => (
                        <div key={choiceIndex} className="flex items-center space-x-2">
                          <span className="text-sm text-gray-500 w-8">
                            {choiceIndex + 1}.
                          </span>
                          {editingQuestionId === question.id ? (
                            <>
                              <input
                                type="text"
                                value={choice}
                                onChange={(e) => updateChoice(question.id, choiceIndex, e.target.value)}
                                className="flex-1 px-3 py-1 border border-gray-300 rounded text-sm"
                                placeholder="Enter claim/option"
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeChoice(question.id, choiceIndex)}
                                icon={<X className="w-3 h-3" />}
                              >
                              </Button>
                            </>
                          ) : (
                            <span className="text-sm text-gray-900">{choice}</span>
                          )}
                        </div>
                      ))}

                      <div className="space-y-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (editingQuestionId !== question.id) {
                              setOriginalQuestionBeforeEdit(question);
                              setEditingQuestionId(question.id);
                            }
                            addChoice(question.id);
                          }}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          Add Claim/Option
                        </Button>
                      </div>
                    </div>

                    {/* Information about BIBD generation */}
                    {question.choices && question.choices.length > 0 && editingQuestionId !== question.id && (
                      <div className="mt-2 text-xs text-blue-600">
                        ℹ️ {question.choices.length} claims will be shown in balanced sets of 4 using MaxDiff algorithm (balanced item frequency & pair co-occurrence for statistical validity)
                      </div>
                    )}

                    {(!question.choices || question.choices.length === 0) && editingQuestionId !== question.id && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded p-2 mt-2">
                        <div className="text-xs text-yellow-800">
                          ⚠️ No claims/options found. MaxDiff requires multiple options to compare.
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Conjoint Analysis settings for conjoint_analysis questions */}
              {hasConjoint(question.type) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Conjoint Analysis Settings
                  </label>

                  {/* Conjoint Description */}
                  {editingQuestionId === question.id && (
                    <div className="mb-4">
                      <label className="block text-xs text-gray-600 mb-1">
                        Question Description
                      </label>
                      <textarea
                        value={question.description || ''}
                        onChange={(e) => updateQuestion(question.id, { description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm resize-none"
                        rows={2}
                        placeholder="Additional context or instructions for this question"
                      />
                    </div>
                  )}

                  {/* Conjoint Attributes */}
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      Attributes & Levels
                    </label>
                    <div className="space-y-3">
                      {(question.conjoint_attributes || []).map((attr, attrIndex) => (
                        <div key={attrIndex} className="border border-gray-200 rounded p-3 bg-gray-50">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-700">
                              Attribute {attrIndex + 1}
                            </span>
                            {editingQuestionId === question.id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeConjointAttribute(question.id, attrIndex)}
                                icon={<X className="w-3 h-3" />}
                              >
                              </Button>
                            )}
                          </div>

                          {editingQuestionId === question.id ? (
                            <>
                              <input
                                type="text"
                                value={attr.name}
                                onChange={(e) => updateConjointAttribute(question.id, attrIndex, 'name', e.target.value)}
                                className="w-full px-3 py-1 border border-gray-300 rounded text-sm mb-2"
                                placeholder="Attribute name (e.g., 'Price', 'Brand')"
                              />
                              <textarea
                                value={attr.levels.join(', ')}
                                onChange={(e) => updateConjointAttribute(question.id, attrIndex, 'levels',
                                  e.target.value.split(',').map(s => s.trim()).filter(s => s)
                                )}
                                className="w-full px-3 py-1 border border-gray-300 rounded text-sm resize-none"
                                rows={2}
                                placeholder="Levels (comma-separated, e.g., '$99, $199, $299')"
                              />
                            </>
                          ) : (
                            <>
                              <div className="text-sm font-medium text-gray-900 mb-1">{attr.name}</div>
                              <div className="text-sm text-gray-600">
                                {attr.levels.join(', ')}
                              </div>
                            </>
                          )}
                        </div>
                      ))}

                      {(!question.conjoint_attributes || question.conjoint_attributes.length === 0) && editingQuestionId !== question.id && (
                        <span className="text-sm text-gray-500 italic">No attributes defined</span>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (editingQuestionId !== question.id) {
                            setOriginalQuestionBeforeEdit(question);
                            setEditingQuestionId(question.id);
                          }
                          addConjointAttribute(question.id);
                        }}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        Add Attribute
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Image Hotspot settings for image_hotspot questions */}
              {hasImageHotspot(question.type) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Image Hotspot Settings
                  </label>
                  
                  {/* Always show editor for image_hotspot if no image, otherwise check edit mode */}
                  {(editingQuestionId === question.id || (!question.image_url && !readOnly)) ? (
                    <ImageHotspotEditor
                      config={{
                        image_url: question.image_url || '',
                        image_path: question.image_path || '',
                        ranking_count: question.ranking_count || 3,
                        bounding_boxes: question.bounding_boxes || [],
                        free_click_mode: question.free_click_mode || false
                      }}
                      onChange={(config: ImageHotspotConfig) => {
                        // FIRST: If uploading image to a question not in edit mode, enter edit mode
                        // This must happen before updateQuestion to prevent editor from closing
                        if (editingQuestionId !== question.id) {
                          setOriginalQuestionBeforeEdit(question);
                          setEditingQuestionId(question.id);
                        }
                        // THEN: Update the question data
                        updateQuestion(question.id, {
                          image_url: config.image_url,
                          image_path: config.image_path,
                          ranking_count: config.ranking_count,
                          bounding_boxes: config.bounding_boxes,
                          free_click_mode: config.free_click_mode
                        });
                      }}
                      disabled={false}
                    />
                  ) : (
                    <div className="space-y-3">
                      {question.image_url ? (() => {
                        // Build full image URL (same logic as ImageHotspotEditor)
                        const API_URL = import.meta.env.VITE_API_URL || '';
                        const fullImageUrl = question.image_url.startsWith('http') 
                          ? question.image_url 
                          : `${API_URL}${question.image_url}`;
                        return (
                          <>
                            {/* Image Preview with Bounding Boxes */}
                            <div className="relative inline-block">
                              <img
                                src={fullImageUrl}
                                alt="Hotspot Preview"
                                className="max-w-md h-auto rounded-lg border border-gray-200"
                                onLoad={(e) => {
                                  const img = e.currentTarget;
                                  img.dataset.width = img.clientWidth.toString();
                                  img.dataset.height = img.clientHeight.toString();
                                }}
                              />
                              {/* Render bounding boxes on image (only for region-based mode) */}
                              {!question.free_click_mode && question.bounding_boxes?.map((box, idx) => (
                                <div
                                  key={box.id || idx}
                                  className="absolute border-2 border-teal-500 bg-teal-500/20 pointer-events-none"
                                  style={{
                                    left: `${box.x * 100}%`,
                                    top: `${box.y * 100}%`,
                                    width: `${box.width * 100}%`,
                                    height: `${box.height * 100}%`
                                  }}
                                >
                                  <div className="absolute -top-5 left-0 bg-teal-500 text-white text-xs px-1.5 py-0.5 rounded whitespace-nowrap">
                                    {box.label}
                                  </div>
                                </div>
                              ))}
                            </div>
                            {/* Summary info */}
                            <div className="text-sm text-gray-600">
                              {question.free_click_mode ? (
                                <>
                                  <span className="text-teal-600">✓</span> Free clicking mode enabled
                                  <span className="mx-2">•</span>
                                </>
                              ) : (
                                <>
                                  <span className="text-green-600">✓</span> {question.bounding_boxes?.length || 0} product region(s) defined
                                  <span className="mx-2">•</span>
                                </>
                              )}
                              Respondents select top <span className="font-medium">{question.ranking_count || 3}</span> {question.free_click_mode ? 'location(s)' : 'choice(s)'}
                            </div>
                          </>
                        );
                      })() : (
                        <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                          <div className="text-sm text-yellow-800">
                            ⚠️ No image uploaded. Click Edit to add an image and define product regions.
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Photo Upload settings for photo_upload questions */}
              {hasPhotoUpload(question.type) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Photo Upload Settings
                  </label>

                  {editingQuestionId === question.id ? (
                    <div className="space-y-3">
                      <p className="text-xs text-gray-500">
                        Respondents will upload photos of their pantry or fridge. AI will automatically extract visible products and brands.
                      </p>

                      {/* Max Images Setting */}
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">
                          Maximum Images
                        </label>
                        <div className="flex items-center space-x-3">
                          <input
                            type="number"
                            min="1"
                            max="10"
                            value={question.max_images || 3}
                            onChange={(e) => {
                              const value = Math.max(1, Math.min(10, parseInt(e.target.value) || 3));
                              handleUpdateQuestion(question.id, { max_images: value });
                            }}
                            className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-teal-500"
                          />
                          <span className="text-xs text-gray-500">photos per response (1-10)</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-600">
                      <span className="text-blue-600">📸</span> Max <span className="font-medium">{question.max_images || 3}</span> photo(s) with AI product extraction
                    </div>
                  )}
                </div>
              )}

              {/* Info Screen settings for info_screen questions */}
              {question.type === 'info_screen' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Info Screen Settings
                  </label>

                  {editingQuestionId === question.id ? (
                    <div className="space-y-3">
                      {/* Display Time Setting */}
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">
                          Minimum Display Time (seconds)
                        </label>
                        <div className="flex items-center space-x-3">
                          <input
                            type="number"
                            min="0"
                            max="300"
                            value={question.display_time_seconds || ''}
                            onChange={(e) => {
                              const value = e.target.value ? parseInt(e.target.value) : undefined;
                              updateQuestion(question.id, { display_time_seconds: value });
                            }}
                            className="w-24 px-3 py-2 border border-gray-300 rounded text-sm"
                            placeholder="0"
                          />
                          <span className="text-sm text-gray-500">seconds</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          If set, the Continue button will be disabled until the timer completes. Leave empty for no timer.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2 text-sm text-gray-600">
                      {question.display_time_seconds ? (
                        <div className="flex items-center space-x-2">
                          <span className="text-blue-600">⏱</span>
                          <span>Display time: <span className="font-medium">{question.display_time_seconds}</span> seconds</span>
                        </div>
                      ) : (
                        <div className="text-gray-500">No minimum display time set</div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* AI Follow-up Toggle - Hidden only for Conjoint Analysis */}
              {question.type !== 'conjoint_analysis' && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      AI Follow-up Questions
                    </label>
                    {editingQuestionId === question.id ? (
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`enable-ai-followup-${question.id}`}
                          checked={question.enable_ai_followup !== false}
                          onChange={(e) => updateQuestion(question.id, { enable_ai_followup: e.target.checked })}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label
                          htmlFor={`enable-ai-followup-${question.id}`}
                          className="text-sm text-gray-700 cursor-pointer"
                        >
                          Enable AI follow-up questions
                        </label>
                      </div>
                    ) : (
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        question.enable_ai_followup
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {question.enable_ai_followup ? 'Enabled' : 'Disabled'}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mb-2">
                    {question.type === 'max_diff'
                      ? 'When enabled, AI will ask follow-up questions about the top 3 most appealing options after all MaxDiff sets are completed.'
                      : 'When enabled, AI may ask follow-up questions based on the respondent\'s answer to get deeper insights.'}
                  </p>
                </div>
              )}

              {/* Follow-up instructions - only show when AI follow-up is enabled */}
              {question.enable_ai_followup && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {UI_TEXT.AI_SURVEY.QUESTIONS_PREVIEW.LABELS.FOLLOWUP_INSTRUCTIONS}
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    {UI_TEXT.AI_SURVEY.QUESTIONS_PREVIEW.DESCRIPTIONS.FOLLOWUP_INSTRUCTIONS_HELP}
                  </p>
                  {editingQuestionId === question.id ? (
                    <textarea
                      value={question.followup_instructions || ''}
                      onChange={(e) => updateQuestion(question.id, { followup_instructions: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md resize-none text-sm"
                      rows={2}
                      placeholder={UI_TEXT.AI_SURVEY.QUESTIONS_PREVIEW.LABELS.FOLLOWUP_INSTRUCTIONS_PLACEHOLDER}
                    />
                  ) : (
                    <div className="bg-gray-50 border border-gray-200 rounded-md p-3 min-h-[2.5rem]">
                      {question.followup_instructions ? (
                        <span className="text-sm text-gray-900">{question.followup_instructions}</span>
                      ) : (
                        <span className="text-sm text-gray-500 italic">No specific follow-up instructions</span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Conditional Display Settings */}
              {index > 0 && (
                <div className="border-t border-gray-200 pt-3 mt-3">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Display Condition
                    </label>
                    {editingQuestionId === question.id ? (
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`enable-condition-${question.id}`}
                          checked={!!question.display_condition}
                          onChange={(e) => {
                            if (e.target.checked) {
                              const eligibleQuestions = questions.slice(0, index).filter(
                                q => DISPLAY_CONDITION_TYPES.includes(q.type)
                              );
                              if (eligibleQuestions.length > 0) {
                                const sourceQ = eligibleQuestions[eligibleQuestions.length - 1];
                                const initialCond = sourceQ.type === 'percentage_allocation'
                                  ? { source_question_id: sourceQ.id, operator: '>' as const, option_key: sourceQ.choices?.[0] || '', threshold: 0, value: '' }
                                  : { source_question_id: sourceQ.id, operator: 'selected' as const, value: sourceQ.choices?.[0] || '' };
                                updateQuestion(question.id, {
                                  display_condition: { logic: 'and', conditions: [initialCond] }
                                });
                              }
                            } else {
                              updateQuestion(question.id, { display_condition: undefined });
                            }
                          }}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label
                          htmlFor={`enable-condition-${question.id}`}
                          className="text-sm text-gray-700 cursor-pointer"
                        >
                          Add display condition
                        </label>
                      </div>
                    ) : (
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        question.display_condition
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {question.display_condition ? 'Conditional' : 'Disabled'}
                      </span>
                    )}
                  </div>

                  {/* Condition Configuration - supports compound AND/OR conditions */}
                  {editingQuestionId === question.id && question.display_condition && (() => {
                    const dc = question.display_condition;
                    const conditions = dc.conditions || (dc.source_question_id ? [{ source_question_id: dc.source_question_id, operator: dc.operator || 'selected', value: dc.value || '', option_key: dc.option_key, threshold: dc.threshold }] : []);
                    const logic = dc.logic || 'and';
                    const eligibleQuestions = questions.slice(0, index).filter(q => DISPLAY_CONDITION_TYPES.includes(q.type));

                    const updateConditions = (newConditions: any[], newLogic?: string) => {
                      updateQuestion(question.id, {
                        display_condition: { logic: (newLogic || logic) as 'and' | 'or', conditions: newConditions }
                      });
                    };

                    const renderConditionRow = (cond: any, condIdx: number) => {
                      const sourceQ = questions.find(q => q.id === cond.source_question_id);
                      const isPct = sourceQ?.type === 'percentage_allocation';
                      return (
                        <div key={condIdx} className="bg-white border border-gray-200 rounded p-2 space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500">Condition {condIdx + 1}</span>
                            {conditions.length > 1 && (
                              <button type="button" onClick={() => updateConditions(conditions.filter((_: any, i: number) => i !== condIdx))} className="text-xs text-red-500 hover:text-red-700">Remove</button>
                            )}
                          </div>
                          <select value={cond.source_question_id} onChange={(e) => {
                            const sq = questions.find(q => q.id === e.target.value);
                            const next = [...conditions];
                            next[condIdx] = sq?.type === 'percentage_allocation'
                              ? { source_question_id: e.target.value, operator: '>', option_key: sq.choices?.[0] || '', threshold: 0, value: '' }
                              : { source_question_id: e.target.value, operator: 'selected', value: sq?.choices?.[0] || '' };
                            updateConditions(next);
                          }} className="w-full px-2 py-1 border border-gray-300 rounded text-sm">
                            {eligibleQuestions.map(q => (
                              <option key={q.id} value={q.id}>Q{questions.indexOf(q) + 1}: {q.text.substring(0, 40)}{q.text.length > 40 ? '...' : ''}</option>
                            ))}
                          </select>
                          {isPct ? (
                            <div className="grid grid-cols-3 gap-1">
                              <select value={cond.option_key || ''} onChange={(e) => { const next = [...conditions]; next[condIdx] = { ...cond, option_key: e.target.value }; updateConditions(next); }} className="px-1 py-1 border border-gray-300 rounded text-xs">
                                {(sourceQ?.choices || []).map((c: string, i: number) => <option key={i} value={c}>{c}</option>)}
                              </select>
                              <select value={cond.operator || '>'} onChange={(e) => { const next = [...conditions]; next[condIdx] = { ...cond, operator: e.target.value }; updateConditions(next); }} className="px-1 py-1 border border-gray-300 rounded text-xs">
                                <option value=">">&gt;</option><option value=">=">&ge;</option><option value="=">=</option><option value="<">&lt;</option><option value="<=">&le;</option>
                              </select>
                              <input type="number" min="0" value={cond.threshold ?? 0} onChange={(e) => { const next = [...conditions]; next[condIdx] = { ...cond, threshold: parseInt(e.target.value) || 0 }; updateConditions(next); }} className="px-1 py-1 border border-gray-300 rounded text-xs" />
                            </div>
                          ) : (
                            <div className="grid grid-cols-2 gap-1">
                              <select value={cond.operator || 'selected'} onChange={(e) => { const next = [...conditions]; next[condIdx] = { ...cond, operator: e.target.value }; updateConditions(next); }} className="px-1 py-1 border border-gray-300 rounded text-xs">
                                <option value="selected">selected</option><option value="not_selected">not selected</option>
                              </select>
                              <select value={cond.value || ''} onChange={(e) => { const next = [...conditions]; next[condIdx] = { ...cond, value: e.target.value }; updateConditions(next); }} className="px-1 py-1 border border-gray-300 rounded text-xs">
                                {(sourceQ?.choices || []).map((c: string, i: number) => <option key={i} value={c}>{c}</option>)}
                              </select>
                            </div>
                          )}
                        </div>
                      );
                    };

                    return (
                      <div className="bg-gray-50 border border-gray-200 rounded-md p-3 space-y-3">
                        <p className="text-xs text-gray-600">Only show this question if:</p>
                        {conditions.length > 1 && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">Match:</span>
                            <select value={logic} onChange={(e) => updateConditions(conditions, e.target.value)} className="px-2 py-1 border border-gray-300 rounded text-xs font-medium">
                              <option value="and">ALL conditions (AND)</option>
                              <option value="or">ANY condition (OR)</option>
                            </select>
                          </div>
                        )}
                        <div className="space-y-2">
                          {conditions.map((cond: any, condIdx: number) => (
                            <React.Fragment key={condIdx}>
                              {condIdx > 0 && (
                                <div className="text-center text-xs font-medium text-gray-400 uppercase">{logic}</div>
                              )}
                              {renderConditionRow(cond, condIdx)}
                            </React.Fragment>
                          ))}
                        </div>
                        {eligibleQuestions.length > 0 && (
                          <button type="button" onClick={() => {
                            const sq = eligibleQuestions[eligibleQuestions.length - 1];
                            const newCond = sq.type === 'percentage_allocation'
                              ? { source_question_id: sq.id, operator: '>', option_key: sq.choices?.[0] || '', threshold: 0, value: '' }
                              : { source_question_id: sq.id, operator: 'selected', value: sq.choices?.[0] || '' };
                            updateConditions([...conditions, newCond]);
                          }} className="text-xs text-blue-600 hover:text-blue-800">+ Add condition</button>
                        )}
                      </div>
                    );
                  })()}

                  {/* Show condition summary when not editing */}
                  {editingQuestionId !== question.id && question.display_condition && (
                    <div className="text-sm text-gray-600">
                      {(() => {
                        const dc = question.display_condition;
                        const conditions = dc.conditions || (dc.source_question_id ? [{ source_question_id: dc.source_question_id, operator: dc.operator, value: dc.value, option_key: dc.option_key, threshold: dc.threshold }] : []);
                        const logic = dc.logic || 'and';

                        const summaries = conditions.map((cond: any) => {
                          const sourceQ = questions.find(q => q.id === cond.source_question_id);
                          const sourceIdx = sourceQ ? questions.indexOf(sourceQ) + 1 : '?';
                          if (sourceQ?.type === 'percentage_allocation') {
                            const opDisplay: Record<string, string> = { '>': '>', '>=': '\u2265', '=': '=', '<': '<', '<=': '\u2264' };
                            return `Q${sourceIdx} "${cond.option_key}" ${opDisplay[cond.operator] || cond.operator} ${cond.threshold}`;
                          }
                          return `Q${sourceIdx} ${cond.operator === 'selected' ? '=' : '\u2260'} "${cond.value}"`;
                        });

                        const joiner = ` ${logic.toUpperCase()} `;
                        return `Show if ${summaries.join(joiner)}`;
                      })()}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
            </React.Fragment>
          ))}
        </div>

        {/* Add New Question Button - only visible when not in readonly mode and not editing */}
        {!readOnly && editingQuestionId === null && (
          <div className="mt-4">
            <Button
              variant="outline"
              onClick={addNewQuestion}
              className="text-blue-600 hover:text-blue-700 border-blue-300"
            >
              + Add Question
            </Button>
          </div>
        )}
      </div>

      {/* Response Quota Configuration - Read Only */}
      {readOnly && (
        <div className="mb-6 border border-gray-200 rounded-lg">
          <div className="p-4">
            <h3 className="text-base font-semibold text-gray-900 mb-3">
              Response Quota
            </h3>
            <div className="text-sm font-medium text-gray-700 mb-1">
              Maximum Responses
            </div>
            <div className="text-sm text-gray-900">
              {maxResponses || 'Unlimited'}
            </div>
          </div>
        </div>
      )}

      {/* Response Quota Configuration - Edit Mode */}
      {!readOnly && (
        <div className="mb-6 border border-gray-200 rounded-lg">
          <button
            type="button"
            onClick={() => setShowQuotaConfig(!showQuotaConfig)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center">
              <h3 className="text-sm font-medium text-gray-900">
                Response Quota (Optional)
              </h3>
            </div>
            <span className="text-gray-400">
              {showQuotaConfig ? '−' : '+'}
            </span>
          </button>

          {showQuotaConfig && (
            <div className="px-4 pb-4 space-y-4">
              <div>
                <label htmlFor="maxResponses" className="block text-sm font-medium text-gray-700 mb-1">
                  Maximum Responses
                </label>
                <input
                  type="number"
                  id="maxResponses"
                  value={maxResponses}
                  onChange={(e) => setMaxResponses(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="e.g., 100"
                  min="1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Set a maximum number of responses for this survey. Leave empty for unlimited responses. When the quota is reached, new participants will be redirected (if redirect config is set).
                </p>
              </div>

              <div>
                <label htmlFor="minLinkedInConnections" className="block text-sm font-medium text-gray-700 mb-1">
                  <span className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                    </svg>
                    Minimum LinkedIn Connections
                  </span>
                </label>
                <input
                  type="number"
                  id="minLinkedInConnections"
                  value={minLinkedInConnections}
                  onChange={(e) => setMinLinkedInConnections(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="e.g., 50"
                  min="0"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Require respondents to have a minimum number of LinkedIn connections. When set, a special LinkedIn verification link will be available. Leave empty to disable LinkedIn verification.
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Panel Provider Redirect Configuration - Read Only */}
      {readOnly && (
        <div className="mb-6 border border-gray-200 rounded-lg">
          <div className="p-4 space-y-4">
            <h3 className="text-base font-semibold text-gray-900 mb-3">
              Panel Provider Integration
            </h3>

            <div>
              <div className="text-sm font-medium text-gray-700 mb-1">
                Redirect Base URL
              </div>
              <div className="text-sm text-gray-900">
                {redirectBaseUrl || 'Not configured'}
              </div>
            </div>

            <div>
              <div className="text-sm font-medium text-gray-700 mb-1">
                Screen Out URL
              </div>
              <div className="text-sm text-gray-900">
                {screenedOutUrl || 'Not configured (uses base URL)'}
              </div>
            </div>

            <div>
              <div className="text-sm font-medium text-gray-700 mb-1">
                Tracking Parameter Name
              </div>
              <div className="text-sm text-gray-900">
                {trackingParamName || 'Not configured'}
              </div>
            </div>

            <div>
              <div className="text-sm font-medium text-gray-700 mb-1">
                Completion Parameters
              </div>
              <div className="text-sm text-gray-900">
                {completionParams || 'Not configured'}
              </div>
            </div>

            <div>
              <div className="text-sm font-medium text-gray-700 mb-1">
                Screen Out Parameters
              </div>
              <div className="text-sm text-gray-900">
                {screenOutParams || 'Not configured'}
              </div>
            </div>

            <div>
              <div className="text-sm font-medium text-gray-700 mb-1">
                Quota Full Parameters
              </div>
              <div className="text-sm text-gray-900">
                {quotaFullParams || 'Not configured'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Panel Provider Redirect Configuration - Edit Mode */}
      {!readOnly && (
        <div className="mb-6 border border-gray-200 rounded-lg">
          <button
            type="button"
            onClick={() => setShowRedirectConfig(!showRedirectConfig)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center">
              <h3 className="text-sm font-medium text-gray-900">
                Panel Provider Integration (Optional)
              </h3>
            </div>
            <span className="text-gray-400">
              {showRedirectConfig ? '−' : '+'}
            </span>
          </button>

          {showRedirectConfig && (
            <div className="p-4 pt-0 space-y-4">
              <p className="text-xs text-gray-600 mb-4">
                Configure redirect URLs to send participants back to your panel provider after completing the survey.
              </p>

              {/* Provider Dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Panel Provider
                </label>
                <select
                  value={selectedProvider}
                  onChange={(e) => handleProviderChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">Select Provider...</option>
                  <option value="userinterviews">User Interviews</option>
                  <option value="dynata">Dynata</option>
                  <option value="custom">Custom</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Select a provider to auto-fill configuration fields with recommended values
                </p>
              </div>

              {/* Redirect Base URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Redirect Base URL <span className="text-red-500">*</span>
                </label>
                <input
                  type="url"
                  value={redirectBaseUrl}
                  onChange={(e) => setRedirectBaseUrl(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="https://example.com/survey/complete"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Base URL for redirecting participants (required if using panel provider integration)
                </p>
              </div>

              {/* Screen Out URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Screen Out URL
                </label>
                <input
                  type="url"
                  value={screenedOutUrl}
                  onChange={(e) => setScreenedOutUrl(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="https://example.com/survey/incomplete"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Separate URL for screened out participants (optional, falls back to base URL if not set)
                </p>
              </div>

              {/* Tracking Parameter Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tracking Parameter Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={trackingParamName}
                  onChange={(e) => setTrackingParamName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="iid (for UserInterviews) or psid (for Dynata)"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Parameter name for participant tracking ID (e.g., "iid", "psid")
                </p>
              </div>

              {/* Completion Parameters */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Completion Parameters
                </label>
                <input
                  type="text"
                  value={completionParams}
                  onChange={(e) => setCompletionParams(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="rst=1, basic=YYYY"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Additional parameters for completion redirect (optional)
                </p>
              </div>

              {/* Screen Out Parameters */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Screen Out Parameters
                </label>
                <input
                  type="text"
                  value={screenOutParams}
                  onChange={(e) => setScreenOutParams(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="rst=2"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Parameters for screen-out redirect (optional)
                </p>
              </div>

              {/* Quota Full Parameters */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quota Full Parameters
                </label>
                <input
                  type="text"
                  value={quotaFullParams}
                  onChange={(e) => setQuotaFullParams(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="rst=3"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Parameters for quota-full redirect (optional)
                </p>
              </div>

              {/* Example Configuration */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
                <p className="text-xs font-medium text-blue-900 mb-2">Example Configuration:</p>
                <div className="text-xs text-blue-800 space-y-1">
                  <p><strong>UserInterviews:</strong></p>
                  <p>• Base URL: https://www.userinterviews.com/project-task-events/custom_link_with_redirect/complete</p>
                  <p>• Screen Out URL: https://www.userinterviews.com/project-task-events/custom_link_with_redirect/incomplete</p>
                  <p>• Tracking Param: iid</p>
                  <p className="mt-2"><strong>Dynata:</strong></p>
                  <p>• Base URL: https://dkr1.ssisurveys.com/projects/end</p>
                  <p>• Tracking Param: psid</p>
                  <p>• Completion Params: rst=1, basic=YYYY</p>
                  <p>• Screen Out Params: rst=2</p>
                  <p>• Quota Full Params: rst=3</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Save Changes Button - only in edit mode for existing surveys */}
      {!readOnly && hideActionButtons && (
        <div className="flex justify-end pt-4 border-t border-gray-200">
          <Button
            variant="primary"
            onClick={() => {
              // Build redirect config
              let redirectConfig: RedirectConfig | null = null;
              if (redirectBaseUrl.trim()) {
                const completionParamsObj = parseParams(completionParams);
                const screenOutParamsObj = parseParams(screenOutParams);
                const quotaFullParamsObj = parseParams(quotaFullParams);

                const trackingParam: { [key: string]: string } = {};
                if (trackingParamName.trim()) {
                  trackingParam[trackingParamName.trim()] = '{TRACKING_ID}';
                }

                redirectConfig = {
                  redirect_base_url: redirectBaseUrl.trim(),
                  screened_out_url: screenedOutUrl.trim() || null,
                  tracking_param: trackingParam,
                  completion_params: Object.keys(completionParamsObj).length > 0 ? completionParamsObj : null,
                  screen_out_params: Object.keys(screenOutParamsObj).length > 0 ? screenOutParamsObj : null,
                  quota_full_params: Object.keys(quotaFullParamsObj).length > 0 ? quotaFullParamsObj : null,
                };
              }

              // Parse maxResponses and minLinkedInConnections
              const maxResponsesValue = maxResponses.trim() ? parseInt(maxResponses) : null;
              const minLinkedInConnectionsValue = minLinkedInConnections.trim() ? parseInt(minLinkedInConnections) : null;

              // Save all changes and exit edit mode
              onQuestionsUpdated(questions, redirectConfig, maxResponsesValue, blocks, minLinkedInConnectionsValue);
              onGoBack(); // Exit edit mode
            }}
            icon={<Save className="w-4 h-4" />}
            disabled={isLoading}
          >
            Save Changes
          </Button>
        </div>
      )}

      {/* Actions */}
      {!readOnly && !hideActionButtons && (
        <div className="flex justify-between items-center pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-600">
            {UI_TEXT.AI_SURVEY.QUESTIONS_PREVIEW.DESCRIPTIONS.QUESTIONS_COUNT(questions.length)}
          </p>

          <div className="flex space-x-3">
            <Button
              variant="outline"
              onClick={onGoBack}
            >
              {UI_TEXT.AI_SURVEY.QUESTIONS_PREVIEW.BUTTONS.RE_ENTER_QUESTIONS}
            </Button>
            <Button
              variant="primary"
              onClick={handleCreateSurvey}
              disabled={editingQuestionId !== null || isSubmitting}
            >
              {isSubmitting ? UI_TEXT.AI_SURVEY.QUESTIONS_PREVIEW.BUTTONS.CREATING_SURVEY : UI_TEXT.AI_SURVEY.QUESTIONS_PREVIEW.BUTTONS.CREATE_SURVEY}
            </Button>
          </div>
        </div>
      )}

      {/* Video Preview Modal */}
      {videoPreviewUrl && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setVideoPreviewUrl(null)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-8 pointer-events-none">
            <div className="bg-white rounded-xl shadow-2xl overflow-hidden max-w-2xl w-full pointer-events-auto">
              <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200">
                <span className="text-sm font-medium text-gray-700">Video Preview</span>
                <button type="button" onClick={() => setVideoPreviewUrl(null)} className="w-6 h-6 flex items-center justify-center rounded hover:bg-gray-100">
                  <span className="text-gray-500 text-lg">&times;</span>
                </button>
              </div>
              <div className="aspect-video">
                {isExternalStreamingVideoUrl(videoPreviewUrl) ? (
                  <iframe
                    src={videoPreviewUrl}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <video
                    src={videoPreviewUrl}
                    controls
                    autoPlay
                    className="w-full h-full bg-black"
                  />
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SurveyQuestionsPreview;
